/**
 * DLC_01 · Arkusz AI — analytical grid over ZE data with AI-computed columns.
 *
 * Rows = a saved snapshot of one dimension (ZE project / branch / coordinator /
 * contractor). Columns: SYSTEM (live portfolio fields), KOD (ARON-generated
 * script, token-free refresh) or ANALIZA (per-cell AI run). Refresh and column
 * planning run as bg tasks — the page polls and refetches the grid.
 * Access: technical directors + CEO (backend enforces; the page only explains).
 * Layout and copy mirror the approved demo artifact ("Arkusz AI").
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  ArrowDown,
  Eye,
  EyeOff,
  ArrowLeftRight,
  ArrowUp,
  ArrowUpDown,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Copy,
  ExternalLink,
  Filter,
  Info,
  Lock,
  MoreHorizontal,
  Plus,
  RefreshCw,
  Send,
  Share2,
  Trash2,
  X,
} from "lucide-react";
import { useAuthStore } from "@/store/auth";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { PythonCodeBlock } from "@/components/ui/PythonCodeEditor";
import { formatDateTime } from "@/utils/formatDate";
import { SectionToolbar } from "../components/ui";
import { useTaskPolling } from "../hooks";
import {
  useRowCandidates,
  useShareEligible,
  useSheetData,
  useSheetMutations,
  useSheetOptions,
  useSetBudget,
  useSheets,
  useSpend,
} from "./hooks";
import type {
  AiChatMessage,
  AiColumnPlan,
  SpendInfo,
  AiSheet,
  AiSheetCell,
  AiSheetColumn,
  AiSheetData,
  AiSheetDimension,
  AiSheetRow,
} from "./types";
import {
  AGG_LABEL,
  aggOptions,
  clampWidth,
  cmpVals,
  computeAgg,
  defaultAgg,
  daysUntil,
  DEFAULT_COL_WIDTH,
  deadlineTone,
  filterActive,
  fmtCellValue,
  fmtDatePL,
  isStale,
  loadAggs,
  loadHidden,
  loadWidths,
  matchColFilter,
  previousLabel,
  saveAggs,
  saveHidden,
  saveWidths,
  type AggKind,
  type ColFilter,
} from "./utils";

/* ── small shared bits ─────────────────────────────────────────────────────── */

const KIND_BADGE: Record<string, { label: string; cls: string; title?: string }> = {
  system: { label: "sys", cls: "bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)] border border-[var(--color-border)]" },
  code: {
    label: "kod", cls: "bg-[#111] text-[#eee] border border-[#333]",
    title: "Skrypt wygenerowany przez ARON-a — odświeżanie bez tokenów",
  },
  analysis: {
    label: "analiza", cls: "bg-red-50 text-red-700 border border-red-200",
    title: "Każde odświeżenie = analiza AI (koszt na komórkę)",
  },
  manual: {
    label: "ręczna", cls: "bg-blue-50 text-blue-700 border border-blue-200",
    title: "Wpisujesz sam — odświeżanie nigdy tego nie nadpisze; kolumny po prawej mogą z tego korzystać",
  },
};

function KindBadge({ kind }: { kind: string }) {
  const b = KIND_BADGE[kind] ?? KIND_BADGE.system;
  return (
    <span
      title={b.title}
      className={`inline-flex px-1.5 text-[0.6rem] font-mono font-bold uppercase tracking-wide whitespace-nowrap ${b.cls}`}
    >
      {b.label}
    </span>
  );
}

const LEVEL_CHIP: Record<string, string> = {
  low: "bg-emerald-50 text-emerald-700 border-emerald-200",
  mid: "bg-amber-50 text-amber-700 border-amber-200",
  high: "bg-red-50 text-red-700 border-red-200",
};

const fmtUsd = (v: number | string) => "$" + Number(v).toFixed(3).replace(".", ",");

/* Example column specs — the demo's "przykłady" chips, kept verbatim. */
const CANNED: { name: string; desc: string }[] = [
  {
    name: "% fakturowania",
    desc: "kwota faktur PRZ podpiętych do zamówień tego przedsięwzięcia jako procent wartości umowy",
  },
  {
    name: "Sygnały sporu",
    desc: "przejrzyj korespondencję i notatki postępowań; wskaż, czy jest ryzyko sporu z właścicielem działki (tak/nie + jedno zdanie)",
  },
  {
    name: "Dni bez postępu",
    desc: "ile dni minęło od ostatniego zdarzenia w historii postępów przedsięwzięcia",
  },
];

/* ── cell rendering ────────────────────────────────────────────────────────── */

function CellContent({ col, cell }: { col: AiSheetColumn; cell: AiSheetCell | undefined }) {
  if (!cell || cell.status === "pending") {
    return (
      <div className="space-y-1 animate-pulse" title="W trakcie liczenia">
        <div className="h-3 w-4/5 bg-[var(--color-bg-elevated)]" />
        <div className="h-2 w-1/2 bg-[var(--color-bg-elevated)]" />
      </div>
    );
  }
  if (cell.status === "no_access") {
    return (
      <span className="inline-flex items-center gap-1 font-mono text-[0.66rem] text-[var(--color-text-muted)]">
        <Lock size={11} /> brak dostępu
      </span>
    );
  }
  if (cell.status === "error") {
    return (
      <span className="font-mono text-[0.66rem] text-red-700" title={cell.error}>
        błąd — szczegóły w komórce
      </span>
    );
  }
  const was = previousLabel(col.fmt, cell.previous);
  const stale = isStale(cell.computed_at);
  const main = fmtCellValue(col.fmt, cell.value);
  const numeric = ["number", "money", "percent"].includes(col.fmt);
  return (
    <div className="flex flex-col gap-0.5">
      {col.fmt === "level" || col.fmt === "flag" ? (
        <span
          className={`self-start px-1.5 border font-mono text-[0.62rem] font-bold uppercase ${
            col.fmt === "flag"
              ? (String(cell.value?.value) === "tak" ? LEVEL_CHIP.high : LEVEL_CHIP.low)
              : (LEVEL_CHIP[String(cell.value?.level ?? cell.value?.value)] ?? "bg-[var(--color-bg-elevated)]")
          }`}
        >
          {main}
          {stale && <StaleDot />}
        </span>
      ) : (
        <span className={`font-mono tabular-nums break-words ${numeric ? "text-right font-semibold" : ""}`}>
          {main}
          {stale && <StaleDot />}
        </span>
      )}
      {cell.value?.detail && (
        <span
          className={`text-[0.66rem] text-[var(--color-text-secondary)] leading-tight line-clamp-2 ${numeric ? "text-right font-mono" : ""}`}
          title={cell.value.detail}
        >
          {cell.value.detail}
        </span>
      )}
      {was && (
        <span
          className="self-end px-1 border border-amber-200 bg-amber-50 text-amber-700 font-mono text-[0.6rem] whitespace-nowrap"
          title="Wartość przed ostatnim odświeżeniem"
        >
          było: <s className="text-[var(--color-text-muted)]">{was}</s>
        </span>
      )}
    </div>
  );
}

/** One hand-filled cell: click to edit, Enter/blur saves, Esc cancels. Typed by the
 * column format so a money column cannot swallow "mniej więcej dużo". */
function ManualCell({ col, cell, onSave }: {
  col: AiSheetColumn; cell: AiSheetCell | undefined;
  onSave: (v: string | number | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const raw = cell?.value?.value;
  const [draft, setDraft] = useState(raw === null || raw === undefined ? "" : String(raw));
  useEffect(() => {
    setDraft(raw === null || raw === undefined ? "" : String(raw));
  }, [raw]);
  const commit = () => {
    setEditing(false);
    const trimmed = draft.trim();
    const current = raw === null || raw === undefined ? "" : String(raw);
    if (trimmed !== current) onSave(trimmed === "" ? null : trimmed);
  };
  if (!editing) {
    return (
      <button
        className="w-full text-left min-h-[18px] hover:outline hover:outline-1 hover:outline-[var(--color-border-strong)] -outline-offset-1"
        title="Kliknij, żeby wpisać"
        onClick={(e) => { e.stopPropagation(); setEditing(true); }}
      >
        {raw === null || raw === undefined || raw === "" ? (
          <span className="text-[var(--color-text-muted)]">— wpisz —</span>
        ) : (
          <span className={`break-words ${["number", "money", "percent"].includes(col.fmt)
            ? "font-mono tabular-nums font-semibold block text-right" : ""}`}>
            {fmtCellValue(col.fmt, cell?.value ?? null)}
          </span>
        )}
      </button>
    );
  }
  const common = {
    autoFocus: true,
    className: "input-base w-full text-xs py-0.5",
    value: draft,
    onClick: (e: React.MouseEvent) => e.stopPropagation(),
    onBlur: commit,
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === "Enter") commit();
      if (e.key === "Escape") { setEditing(false); setDraft(String(raw ?? "")); }
    },
  };
  if (col.fmt === "flag" || col.fmt === "level") {
    const opts = col.fmt === "flag"
      ? [["", "—"], ["tak", "tak"], ["nie", "nie"]]
      : [["", "—"], ["low", "niskie"], ["mid", "średnie"], ["high", "wysokie"]];
    return (
      <select {...common} onChange={(e) => { setDraft(e.target.value); }}>
        {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    );
  }
  return (
    <input
      {...common}
      type={col.fmt === "date" ? "date" : "text"}
      inputMode={["number", "money", "percent"].includes(col.fmt) ? "decimal" : undefined}
      placeholder={["number", "money", "percent"].includes(col.fmt) ? "liczba" : ""}
      onChange={(e) => setDraft(e.target.value)}
    />
  );
}

const StaleDot = () => (
  <span
    className="inline-block w-1.5 h-1.5 bg-amber-500 ml-1 align-middle"
    title="Wynik starszy niż 5 dni — odśwież"
  />
);

/* ── drawers ───────────────────────────────────────────────────────────────── */

type DrawerState =
  | { type: "cell"; rowId: string; colId: string }
  | { type: "column"; colId: string }
  | { type: "info" }
  | null;

function Drawer({ title, onClose, children, footer }: {
  title: string; onClose: () => void; children: React.ReactNode; footer?: React.ReactNode;
}) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);
  return (
    <aside
      className="fixed top-0 right-0 bottom-0 w-[560px] max-w-[96vw] bg-[var(--color-bg-surface)] border-l-2 border-[var(--color-text-primary)] z-50 flex flex-col"
      style={{ boxShadow: "-14px 0 32px rgba(10,10,10,0.18)" }}
    >
      <div className="flex items-center gap-2 px-4 py-3 border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
        <h2 className="font-mono text-[0.72rem] font-bold uppercase tracking-widest">{title}</h2>
        <span className="flex-1" />
        <button className="p-1 hover:text-[var(--color-primary)]" onClick={onClose} aria-label="Zamknij">
          <X size={14} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 text-sm">{children}</div>
      {footer && (
        <div className="flex flex-wrap gap-2 px-4 py-3 border-t border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
          {footer}
        </div>
      )}
    </aside>
  );
}

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <h3 className="section-label border-b border-[var(--color-border)] pb-1 mt-4 mb-2">{children}</h3>
);

function SourceList({ sources }: { sources: string[] }) {
  if (!sources?.length) return <p className="text-xs text-[var(--color-text-muted)]">—</p>;
  return (
    <div className="space-y-1">
      {sources.map((s) => (
        <div key={s} className="font-mono text-[0.64rem] bg-[var(--color-bg-elevated)] border border-[var(--color-border)] px-2 py-0.5 break-all text-[var(--color-text-secondary)]">
          {s.startsWith("GET ") ? (<><b className="text-[var(--color-primary)] font-semibold">GET</b>{s.slice(3)}</>) : s}
        </div>
      ))}
    </div>
  );
}

/* Mini sample table — planner's trial rows (modal step 3 + column drawer). */
function SampleTable({ sample }: { sample: NonNullable<AiSheetColumn["plan"]["sample"]> }) {
  return (
    <table className="w-full text-[0.7rem] border border-[var(--color-border)] mt-1.5">
      <tbody>
        {sample.map((s, i) => (
          <tr key={i} className="border-t border-[var(--color-border-muted)]">
            <td className="px-2 py-1">{s.label}</td>
            <td className="px-2 py-1 text-right font-mono font-semibold">
              {s.no_access ? (
                <span className="inline-flex items-center gap-1 text-[var(--color-text-muted)] font-normal"><Lock size={10} /> brak dostępu</span>
              ) : s.ok ? (
                <>{s.value ?? "—"}{s.detail ? <span className="font-normal text-[var(--color-text-muted)]"> {s.detail}</span> : null}</>
              ) : (
                <span className="text-red-700" title={s.error}>błąd</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* ── page ──────────────────────────────────────────────────────────────────── */

/* Zakładka „Arkusz AI" strony „Kontrola procesu" (/power-projects/kontrola?tab=arkusz-ai). */
export function AiSheetSection() {
  const user = useAuthStore((s) => s.user);
  const allowed = !!user && (user.is_ceo || user.is_technical_director);

  const sheetsQ = useSheets();
  const optionsQ = useSheetOptions();
  const spendQ = useSpend();
  const [activeId, setActiveId] = useState<string | null>(null);
  const sheets = sheetsQ.data ?? [];
  const active = sheets.find((s) => s.id === activeId) ?? sheets[0] ?? null;
  const dataQ = useSheetData(active?.id ?? null);
  const mut = useSheetMutations(active?.id ?? null);

  const [drawer, setDrawer] = useState<DrawerState>(null);
  const [showNewSheet, setShowNewSheet] = useState(false);
  const [spendOpen, setSpendOpen] = useState(false);
  const [colModal, setColModal] = useState<{ replaceId?: string; label?: string; description?: string } | null>(null);
  const [search, setSearch] = useState("");
  const [banner, setBanner] = useState<React.ReactNode | null>(null);

  // One page-level background task at a time: refresh, or a plan handed off
  // from a closed modal (the modal normally polls its own plan task).
  const [taskId, setTaskId] = useState<string | null>(null);
  const [taskKind, setTaskKind] = useState<"refresh" | "plan" | null>(null);
  const task = useTaskPolling(taskId);
  useEffect(() => {
    const st = task.data?.status;
    if (!st || !["success", "failure", "cancelled"].includes(st)) return;
    const finished = task.data!;
    setTaskId(null);
    mut.invalidate();
    if (taskKind === "plan" && st === "success") {
      const colId = (finished.result as { column_id?: string } | null)?.column_id;
      if (colId) setDrawer({ type: "column", colId });
    } else if (st === "failure") {
      setBanner(<span className="text-red-700">Zadanie nie powiodło się: {finished.error || "nieznany błąd"}</span>);
    } else if (taskKind === "refresh") {
      setBanner(<span><b>Gotowe.</b> Zmienione wartości mają znacznik „było:”.</span>);
    }
    setTaskKind(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task.data?.status]);

  const startTask = (id: string, kind: "refresh" | "plan") => {
    setTaskId(id);
    setTaskKind(kind);
  };

  if (!allowed) {
    return (
      <div className="space-y-4">
        <div className="card p-6 max-w-xl">
          <p className="text-sm">
            Arkusz AI jest dostępny wyłącznie dla <b>dyrektorów technicznych</b> i <b>CEO</b>.
          </p>
        </div>
      </div>
    );
  }

  const data = dataQ.data ?? null;
  const spend = spendQ.data;

  return (
    <div className="space-y-4">
      <SectionToolbar
        text="wiersze = wybrany wymiar danych ZE · kolumny liczy ARON · dyr. techniczny + CEO"
        actions={
          <div className="flex items-center gap-3">
            {spend && (
              <button
                className={`font-mono text-[0.68rem] hover:text-[var(--color-primary)] ${
                  Number(spend.today_usd) >= Number(spend.budget_usd) * 0.9
                    ? "text-[var(--color-primary)] font-bold"
                    : "text-[var(--color-text-secondary)]"
                }`}
                title="Kliknij: rozliczenie dzisiejszych kosztów i zmiana dziennego limitu"
                onClick={() => setSpendOpen(true)}
              >
                dziś: <b>${Number(spend.today_usd).toFixed(2)}</b> / ${Number(spend.budget_usd).toFixed(0)}
              </button>
            )}
            <Button size="sm" variant="ghost" onClick={() => setDrawer({ type: "info" })}>
              <Info size={12} className="mr-1" /> Jak to działa
            </Button>
          </div>
        }
      />

      {/* sheet tabs */}
      <div className="flex flex-wrap items-center gap-1.5">
        {sheets.map((s) => {
          const nCols = s.columns.filter((c) => !c.is_draft).length;
          return (
            <button
              key={s.id}
              onClick={() => { setActiveId(s.id); setDrawer(null); setBanner(null); }}
              className={`px-3 py-1.5 font-mono text-[0.68rem] font-semibold uppercase tracking-wide border ${
                active?.id === s.id
                  ? "bg-[var(--color-bg-surface)] border-[var(--color-text-primary)] border-b-2 border-b-[var(--color-primary)]"
                  : "bg-[var(--color-bg-elevated)] border-[var(--color-border)] text-[var(--color-text-secondary)] hover:border-[var(--color-border-strong)]"
              }`}
            >
              {s.name}
              <span className="ml-2 font-normal text-[var(--color-text-muted)]">
                {s.rows_count}×{nCols}
              </span>
              {!s.is_owner && (
                <span className="ml-1.5 font-normal normal-case text-[var(--color-text-muted)]">
                  od: {s.owner_label}
                </span>
              )}
            </button>
          );
        })}
        <button
          onClick={() => setShowNewSheet(true)}
          className="px-3 py-1.5 font-mono text-[0.68rem] font-semibold uppercase tracking-wide border border-dashed border-[var(--color-border-strong)] text-[var(--color-text-secondary)] hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]"
        >
          + nowy arkusz
        </button>
      </div>

      {sheetsQ.isLoading && <div className="card p-6"><Spinner /></div>}
      {sheetsQ.isError && (
        <div className="card border-l-[3px] border-l-red-600 p-4 text-sm max-w-xl">
          Nie udało się pobrać arkuszy — odśwież stronę.{" "}
          <Button size="sm" variant="ghost" onClick={() => sheetsQ.refetch()}>Spróbuj ponownie</Button>
        </div>
      )}
      {!sheetsQ.isLoading && !sheetsQ.isError && !active && (
        <div className="card p-6 max-w-xl text-sm">
          Nie masz jeszcze arkusza. Utwórz pierwszy: wybierz, czym są wiersze
          (projekt ZE, oddział, koordynator, podwykonawca), a kolumny dodasz potem.
        </div>
      )}

      {active && (
        <SheetView
          key={active.id}
          sheet={active}
          data={data}
          loading={dataQ.isLoading}
          search={search}
          setSearch={setSearch}
          mut={mut}
          options={optionsQ.data}
          banner={banner}
          setBanner={setBanner}
          taskRunning={!!taskId}
          taskLabel={task.data?.log?.split("\n").filter(Boolean).slice(-1)[0]}
          startTask={startTask}
          openDrawer={setDrawer}
          openColModal={setColModal}
        />
      )}

      {/* drawers */}
      {drawer?.type === "info" && <InfoDrawer onClose={() => setDrawer(null)} />}
      {drawer?.type === "column" && active && (
        <ColumnDrawer
          sheet={active}
          colId={drawer.colId}
          data={data}
          mut={mut}
          startTask={startTask}
          setBanner={setBanner}
          onOpenColumn={(cid) => setDrawer({ type: "column", colId: cid })}
          onClose={() => setDrawer(null)}
        />
      )}
      {drawer?.type === "cell" && active && data && (
        <CellDrawer
          data={data}
          rowId={drawer.rowId}
          colId={drawer.colId}
          mut={mut}
          startTask={startTask}
          onClose={() => setDrawer(null)}
        />
      )}

      {/* modals */}
      {spendOpen && spend && (
        <SpendModal spend={spend} onClose={() => setSpendOpen(false)} />
      )}
      {showNewSheet && (
        <NewSheetModal
          options={optionsQ.data}
          mut={mut}
          onCreated={(s) => {
            setShowNewSheet(false);
            setActiveId(s.id);
            if (s.rows_count === 0) {
              setBanner(
                <span>
                  Obiektów pasujących jest więcej niż limit arkusza — zawęź filtry na
                  pasku i kliknij <b>„Aktualizuj wiersze”</b>.
                </span>,
              );
            }
          }}
          onClose={() => setShowNewSheet(false)}
        />
      )}
      {colModal && active && (
        <AddColumnModal
          sheet={active}
          options={optionsQ.data}
          replaceId={colModal.replaceId}
          initialLabel={colModal.label}
          initialDescription={colModal.description}
          mut={mut}
          startRefreshTask={(id) => startTask(id, "refresh")}
          handoffPlanTask={(id) => startTask(id, "plan")}
          onClose={() => setColModal(null)}
        />
      )}
    </div>
  );
}

/* ── sheet view (toolbar + grid) ───────────────────────────────────────────── */

type Mut = ReturnType<typeof useSheetMutations>;

function SheetView({ sheet, data, loading, search, setSearch, mut, options, banner, setBanner, taskRunning, taskLabel, startTask, openDrawer, openColModal }: {
  sheet: AiSheet;
  data: AiSheetData | null;
  loading: boolean;
  search: string;
  setSearch: (s: string) => void;
  mut: Mut;
  options: ReturnType<typeof useSheetOptions>["data"];
  banner: React.ReactNode | null;
  setBanner: (b: React.ReactNode | null) => void;
  taskRunning: boolean;
  taskLabel?: string;
  startTask: (id: string, kind: "refresh" | "plan") => void;
  openDrawer: (d: DrawerState) => void;
  openColModal: (m: { replaceId?: string }) => void;
}) {
  const dim = options?.dimensions.find((d) => d.key === sheet.row_dimension);
  const [addRowOpen, setAddRowOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  // Client-side view state: sort + per-column filters (dynamic, typed by column fmt).
  const [sort, setSort] = useState<{ colId: string; dir: 1 | -1 } | null>(null);
  const [colFilters, setColFilters] = useState<Record<string, ColFilter>>({});
  // Widths are a per-viewer convenience (a shared sheet is read-only, but everyone
  // may size it to their own screen) — kept in localStorage, never on the record.
  const [widths, setWidths] = useState<Record<string, number>>(() => loadWidths(sheet.id));
  const [aggs, setAggs] = useState<Record<string, AggKind>>(() => loadAggs(sheet.id));
  // Excel-style hiding: a view preference, per viewer — the data stays in the sheet.
  const [hidden, setHiddenState] = useState(() => loadHidden(sheet.id));
  const toggleHidden = (kind: "cols" | "rows", id: string) =>
    setHiddenState((prev) => {
      const set = new Set(prev[kind]);
      set.has(id) ? set.delete(id) : set.add(id);
      const next = { ...prev, [kind]: [...set] };
      saveHidden(sheet.id, next);
      return next;
    });
  const showAllHidden = () => {
    const next = { cols: [], rows: [] };
    setHiddenState(next);
    saveHidden(sheet.id, next);
  };
  const setAgg = (colId: string, kind: AggKind) =>
    setAggs((prev) => {
      const next = { ...prev, [colId]: kind };
      saveAggs(sheet.id, next);
      return next;
    });
  const setWidth = (colId: string, px: number | null, persist: boolean) =>
    setWidths((prev) => {
      const next = { ...prev };
      if (px === null) delete next[colId];
      else next[colId] = px;
      if (persist) saveWidths(sheet.id, next);
      return next;
    });
  const readOnly = !sheet.is_owner;

  const allColumns = (data?.sheet.columns ?? sheet.columns).filter((c) => !c.is_draft);
  const columns = allColumns.filter((c) => !hidden.cols.includes(c.id));
  const drafts = readOnly ? [] : (data?.sheet.columns ?? sheet.columns).filter((c) => c.is_draft);

  const colValue = (r: AiSheetRow, c: AiSheetColumn): string | number | null => {
    if (c.kind === "system") {
      const sv = data?.system_values?.[r.id]?.[c.config.field ?? ""];
      if (!sv || sv.no_access) return null;
      return sv.value;
    }
    const cell = cellFor(data, r.id, c.id);
    if (!cell || cell.status !== "ok" || !cell.value) return null;
    if (c.fmt === "level") return (cell.value.level ?? cell.value.value) as string | null;
    return cell.value.value;
  };

  const rows = useMemo(() => {
    let out = data?.rows ?? [];
    if (search.trim()) {
      const q = search.toLowerCase();
      out = out.filter((r) => (r.label + " " + r.sublabel).toLowerCase().includes(q));
    }
    for (const [colId, f] of Object.entries(colFilters)) {
      const c = columns.find((x) => x.id === colId);
      if (!c || !filterActive(f)) continue;
      out = out.filter((r) => matchColFilter(f, c.fmt, colValue(r, c)));
    }
    out = out.filter((r) => !hidden.rows.includes(r.id));
    if (sort) {
      const c = columns.find((x) => x.id === sort.colId);
      if (c) out = [...out].sort((a, b) => cmpVals(colValue(a, c), colValue(b, c)) * sort.dir);
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, search, colFilters, sort, hidden]);

  // Freshest computed_at across the grid + what this sheet's stored results cost
  // (sum of cell costs; CODE columns contribute 0 — that is the point of them).
  const { lastComputed, spent } = useMemo(() => {
    let max: string | null = null;
    let sum = 0;
    for (const byCol of Object.values(data?.cells ?? {})) {
      for (const c of Object.values(byCol)) {
        if (c.computed_at && (!max || c.computed_at > max)) max = c.computed_at;
        sum += Number(c.cost_usd || 0);
      }
    }
    return { lastComputed: max, spent: sum };
  }, [data]);

  const setFilter = (patch: Record<string, unknown>) =>
    mut.patchSheet.mutate({ id: sheet.id, patch: { filters: { ...sheet.filters, ...patch } } });

  const doRefresh = (v: Parameters<Mut["refresh"]["mutate"]>[0]) =>
    mut.refresh.mutate(v, {
      onSuccess: (r) => {
        startTask(r.task_id, "refresh");
        setBanner(
          <span className="inline-flex items-center gap-2">
            <Spinner size="sm" /> <b>Zadanie w tle:</b> liczę {r.cells} komórek (kolumny KOD bez tokenów). Możesz pracować dalej.
          </span>,
        );
      },
      onError: (e) => setBanner(<span className="text-red-700">{apiErrText(e)}</span>),
    });

  const doSync = () =>
    mut.rowsSync.mutate({ apply: false }, {
      onSuccess: (r) => {
        if (!r.to_add.length && !r.missing.length) {
          setBanner(<span>Lista wierszy aktualna — filtry pasują do {r.matches} obiektów i wszystkie są w arkuszu.</span>);
          return;
        }
        setBanner(
          <span className="inline-flex flex-wrap items-center gap-2">
            {r.to_add.length > 0 && (
              <>
                <span>
                  Filtry pasują do <b>{r.matches}</b> obiektów — <b>{r.to_add.length}</b> nowych nie ma
                  w arkuszu (np. {r.to_add[0].label}). Wiersze są zapisane — dochodzą tylko za Twoją zgodą.
                </span>
                <Button size="sm" onClick={() =>
                  mut.rowsSync.mutate({ apply: true }, {
                    onSuccess: (rr) => setBanner(<span><b>Dodano {rr.added.length} wierszy</b> — odśwież kolumny AI, żeby je policzyć.</span>),
                  })
                }>
                  Dodaj {r.to_add.length}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setBanner(null)}>Ignoruj</Button>
              </>
            )}
            {r.missing.length > 0 && (
              <span className="text-[var(--color-text-muted)]">
                ({r.missing.length} zapisanych wierszy nie istnieje już w systemie)
              </span>
            )}
          </span>,
        );
      },
    });

  return (
    <div className="space-y-2">
      {/* toolbar */}
      <div className="card p-2.5 flex flex-wrap items-end gap-2">
        {dim?.filters.includes("branch") && (
          <label className="block">
            <span className="section-label">Oddział</span>
            <select
              className="input-base block min-w-[130px]"
              disabled={readOnly}
              value={sheet.filters.branch ?? ""}
              onChange={(e) => setFilter({ branch: e.target.value || undefined })}
            >
              <option value="">Wszystkie</option>
              {options?.filter_options.branches.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </label>
        )}
        {dim?.filters.includes("coordinator") && (
          <label className="block">
            <span className="section-label">Koordynator</span>
            <select
              className="input-base block min-w-[150px]"
              disabled={readOnly}
              value={sheet.filters.coordinator ?? ""}
              onChange={(e) => setFilter({ coordinator: e.target.value || undefined })}
            >
              <option value="">Wszyscy</option>
              {options?.filter_options.coordinators.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </label>
        )}
        {dim?.filters.includes("active_only") && (
          <label className="inline-flex items-center gap-1.5 pb-2 text-xs text-[var(--color-text-secondary)] cursor-pointer">
            <input
              type="checkbox"
              className="accent-[var(--color-primary)]"
              disabled={readOnly}
              checked={sheet.filters.active_only !== false}
              onChange={(e) => setFilter({ active_only: e.target.checked })}
            />
            tylko aktywne
          </label>
        )}
        <label className="block">
          <span className="section-label">Szukaj (w widoku)</span>
          <input
            className="input-base block min-w-[170px]"
            placeholder="nazwa…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
        <span className="flex-1" />
        {readOnly ? (
          <>
            <span className="inline-flex items-center gap-1.5 px-2 py-1 border border-[var(--color-border)] bg-[var(--color-bg-elevated)] font-mono text-[0.64rem] uppercase text-[var(--color-text-secondary)]">
              <Share2 size={11} /> od: {sheet.owner_label} · tylko odczyt
            </span>
            <Button size="sm" loading={mut.copySheet.isPending}
              title="Twoja własna, edytowalna kopia — od teraz liczy się na TWOICH uprawnieniach"
              onClick={() => mut.copySheet.mutate(sheet.id)}>
              <Copy size={12} className="mr-1" /> Kopiuj do siebie
            </Button>
          </>
        ) : (
          <>
            <Button size="sm" variant="ghost" onClick={doSync} loading={mut.rowsSync.isPending}
              title="Wiersze są zapisane na stałe — nowe obiekty spełniające filtry dochodzą dopiero za Twoją zgodą">
              ↺ Aktualizuj wiersze
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setAddRowOpen(true)}>+ wiersz</Button>
            <Button
              size="sm" variant="secondary"
              onClick={() => mut.patchSheet.mutate({ id: sheet.id, patch: { transposed: !sheet.transposed } })}
              title="Obrót o 90° — zamiana wierszy i kolumn (widok, nie dane)"
            >
              <ArrowLeftRight size={12} className="mr-1" />
              {sheet.transposed ? "Widok zwykły" : "Transponuj 90°"}
            </Button>
            <Button size="sm" variant="secondary" onClick={() => doRefresh({ scope: "all" })} disabled={taskRunning}
              title="Przelicza wszystkie kolumny AI dla wszystkich wierszy — zadanie w tle">
              <RefreshCw size={12} className="mr-1" /> Odśwież wszystko
            </Button>
            <Button size="sm" onClick={() => openColModal({})}>
              <Plus size={12} className="mr-1" /> Kolumna
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setShareOpen(true)}
              title="Udostępnij arkusz (tylko do odczytu) innemu dyrektorowi technicznemu lub CEO">
              <Share2 size={12} className="mr-1" />
              {sheet.shared_with.length > 0 ? `Udostępniony (${sheet.shared_with.length})` : "Udostępnij"}
            </Button>
            <Button
              size="sm" variant="ghost"
              title="Usuń arkusz (wiersze i wyniki znikają; dane źródłowe zostają)"
              onClick={() => {
                if (window.confirm(`Usunąć arkusz „${sheet.name}”?`)) mut.deleteSheet.mutate(sheet.id);
              }}
            >
              <Trash2 size={12} />
            </Button>
          </>
        )}
      </div>

      {(hidden.cols.length > 0 || hidden.rows.length > 0) && (
        <div className="card border-l-[3px] border-l-[var(--color-border-strong)] px-3 py-1.5 text-xs flex items-center gap-2">
          <EyeOff size={12} className="text-[var(--color-text-muted)]" />
          <span>
            Ukryte:{" "}
            {hidden.cols.length > 0 && <b>{hidden.cols.length} kolumn</b>}
            {hidden.cols.length > 0 && hidden.rows.length > 0 && ", "}
            {hidden.rows.length > 0 && <b>{hidden.rows.length} wierszy</b>}
            {" "}(dane zostają w arkuszu — to tylko widok)
          </span>
          <span className="flex-1" />
          <Button size="sm" variant="ghost" onClick={showAllHidden}>
            <Eye size={12} className="mr-1" /> Odkryj wszystko
          </Button>
        </div>
      )}

      {/* draft banners */}
      {drafts.map((d) => (
        <div key={d.id} className="card border-l-[3px] border-l-[var(--color-primary)] px-3 py-2 flex items-center gap-2 text-sm">
          <KindBadge kind={d.plan.mode ?? d.kind} />
          <span>
            Szkic kolumny <b>„{d.label}”</b> gotowy — obejrzyj decyzję ARON-a i próbkę, zanim policzy całość.
          </span>
          <span className="flex-1" />
          <Button size="sm" variant="secondary" onClick={() => openDrawer({ type: "column", colId: d.id })}>
            Zobacz szkic
          </Button>
        </div>
      ))}

      {(banner || taskRunning) && (
        <div className="card border-l-[3px] border-l-[var(--color-border-strong)] px-3 py-2 text-sm flex items-center gap-2">
          {taskRunning && !banner && (
            <span className="inline-flex items-center gap-2"><Spinner size="sm" /> {taskLabel || "Pracuję w tle…"}</span>
          )}
          {banner}
          <span className="flex-1" />
          <button className="p-0.5 hover:text-[var(--color-primary)]" onClick={() => setBanner(null)} aria-label="Zamknij">
            <X size={12} />
          </button>
        </div>
      )}

      {/* grid */}
      <div className="card">
        <div className="overflow-x-auto">
          {loading && !data ? (
            <div className="p-6"><Spinner /></div>
          ) : sheet.transposed ? (
            <TransposedGrid sheet={sheet} rows={rows} columns={columns} data={data} doRefresh={doRefresh} openDrawer={openDrawer} readOnly={readOnly} />
          ) : (
            <NormalGrid
              sheet={sheet} rows={rows} columns={columns} data={data} mut={mut}
              doRefresh={doRefresh} openDrawer={openDrawer} readOnly={readOnly}
              sort={sort} setSort={setSort}
              colFilters={colFilters} setColFilters={setColFilters}
              widths={widths} setWidth={setWidth}
              aggs={aggs} setAgg={setAgg}
              onHideColumn={(id) => toggleHidden("cols", id)}
              onHideRow={(id) => toggleHidden("rows", id)}
              onMoveError={(m) => setBanner(<span className="text-red-700">{m}</span>)}
            />
          )}
        </div>
      </div>

      {/* legend */}
      <div className="flex flex-wrap items-center gap-4 font-mono text-[0.62rem] text-[var(--color-text-muted)] px-1">
        <span className="inline-flex items-center gap-1"><KindBadge kind="system" /> pole portfela (na żywo)</span>
        <span className="inline-flex items-center gap-1"><KindBadge kind="code" /> skrypt ARON-a — odświeżanie bez tokenów</span>
        <span className="inline-flex items-center gap-1"><KindBadge kind="analysis" /> analiza AI przy każdym odświeżeniu</span>
        <span className="inline-flex items-center gap-1"><KindBadge kind="manual" /> wpisujesz sam — odświeżanie tego nie rusza</span>
        <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 bg-amber-500 inline-block" /> wynik starszy niż 5 dni</span>
        <span className="inline-flex items-center gap-1"><Lock size={10} /> brak dostępu (uprawnienia)</span>
        <span>„było:” = wartość sprzed odświeżenia</span>
        {lastComputed && (
          <span className="ml-auto" title="Suma kosztu policzonych komórek tego arkusza; kolumny KOD kosztują 0">
            ostatnie przeliczenie: {formatDateTime(lastComputed)} · koszt wyników:{" "}
            <b className="text-[var(--color-text-secondary)]">{fmtUsd(spent)}</b>
          </span>
        )}
      </div>

      {addRowOpen && (
        <AddRowModal sheetId={sheet.id} mut={mut} onClose={() => setAddRowOpen(false)} />
      )}
      {shareOpen && (
        <ShareModal sheet={sheet} mut={mut} onClose={() => setShareOpen(false)} />
      )}
    </div>
  );
}

/** Read-only share management: eligible recipients (dyr tech + CEO) with a toggle. */
function ShareModal({ sheet, mut, onClose }: { sheet: AiSheet; mut: Mut; onClose: () => void }) {
  const eligible = useShareEligible(sheet.id, true);
  return (
    <ModalShell title={`Udostępnij · ${sheet.name}`} onClose={onClose}
      footer={<Button size="sm" variant="ghost" onClick={onClose}>Zamknij</Button>}>
      <p className="text-[0.7rem] text-[var(--color-text-muted)] mb-2">
        Odbiorca widzi arkusz i wyniki <b>tylko do odczytu</b> (to Twój raport — wartości
        policzone na Twoich uprawnieniach). Może go skopiować do siebie — kopia liczy się
        już na JEGO uprawnieniach.
      </p>
      {eligible.isLoading && <div className="py-3"><Spinner size="sm" /></div>}
      <div className="divide-y divide-[var(--color-border-muted)] max-h-72 overflow-y-auto">
        {(eligible.data ?? []).map((u) => (
          <label key={u.id} className="flex items-center gap-2 py-1.5 text-xs cursor-pointer">
            <input
              type="checkbox"
              className="accent-[var(--color-primary)]"
              checked={u.shared}
              disabled={mut.share.isPending}
              onChange={(e) => mut.share.mutate({ userId: u.id, on: e.target.checked })}
            />
            {u.name}
          </label>
        ))}
        {eligible.data?.length === 0 && (
          <p className="py-3 text-xs text-[var(--color-text-muted)]">
            Brak innych osób z dostępem do Arkusza AI (dyr. techniczni + CEO).
          </p>
        )}
      </div>
    </ModalShell>
  );
}

/* ── grids ─────────────────────────────────────────────────────────────────── */

// No `whitespace-nowrap`: a header that refuses to wrap sets a floor on the column
// width no matter what the user drags it to.
const thCls = "text-left px-2 py-2 font-mono text-[0.62rem] font-bold uppercase tracking-widest text-[var(--color-text-muted)] bg-[var(--color-bg-elevated)] align-top";
const stickyCls = "sticky z-10 border-r border-[var(--color-border-strong)]";
/** Ordinal column — narrow on purpose; it is a reference marker, not data. */
const LP_WIDTH = 38;

function cellFor(data: AiSheetData | null, rowId: string, colId: string): AiSheetCell | undefined {
  return data?.cells?.[rowId]?.[colId];
}

function RowHead({ row, mut, doRefresh, readOnly, canMove, onHide }: {
  row: AiSheetRow; mut: Mut; doRefresh: (v: { scope: "row"; row_id: string }) => void;
  readOnly: boolean; canMove: boolean; onHide: () => void;
}) {
  return (
    <div className="relative flex items-start gap-1.5 group pr-1">
      <div className={`min-w-0 ${row.archived ? "text-[var(--color-text-muted)]" : ""}`}>
        <div className="font-semibold text-[0.76rem] leading-tight break-words">
          {row.label}
          {row.archived && <Badge color="gray" className="ml-1">archiwum</Badge>}
          {row.missing && <Badge color="gray" className="ml-1">nie istnieje</Badge>}
        </div>
        <div className="font-mono text-[0.62rem] text-[var(--color-text-muted)] break-words">{row.sublabel}</div>
      </div>
      <span className="absolute top-0.5 right-2 hidden group-hover:flex gap-0.5 bg-[var(--color-bg-surface)] border border-[var(--color-border)] px-0.5 z-20 shadow-sm">
        {canMove && (
          <span className="flex flex-col -my-0.5">
            <button className="px-1 text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
              title="Przesuń wiersz wyżej"
              onClick={() => mut.moveRow.mutate({ rowId: row.id, direction: "up" })}>
              <ChevronUp size={10} />
            </button>
            <button className="px-1 text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
              title="Przesuń wiersz niżej"
              onClick={() => mut.moveRow.mutate({ rowId: row.id, direction: "down" })}>
              <ChevronDown size={10} />
            </button>
          </span>
        )}
        {!readOnly && (
          <button
            className="p-1 text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
            title="Odśwież wiersz (kolumny AI)"
            onClick={() => doRefresh({ scope: "row", row_id: row.id })}
          >
            <RefreshCw size={11} />
          </button>
        )}
        <button
          className="p-1 text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
          title="Ukryj wiersz (zostaje w arkuszu)"
          onClick={onHide}
        >
          <EyeOff size={11} />
        </button>
        {row.href && (
          <Link
            to={row.href}
            className="p-1 text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
            title="Otwórz kartę"
          >
            <ExternalLink size={11} />
          </Link>
        )}
        {!readOnly && (
          <button
            className="p-1 text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
            title="Usuń wiersz z arkusza (obiekt zostaje w systemie)"
            onClick={() => mut.rowsRemove.mutate(row.id)}
          >
            <X size={11} />
          </button>
        )}
      </span>
    </div>
  );
}

interface ColHeadProps {
  col: AiSheetColumn;
  onHide?: () => void;
  doRefresh: (v: { scope: "column"; column_id: string }) => void;
  openDrawer: (d: DrawerState) => void;
  sort?: { colId: string; dir: 1 | -1 } | null;
  setSort?: (s: { colId: string; dir: 1 | -1 } | null) => void;
  filter?: ColFilter;
  setFilter?: (f: ColFilter | null) => void;
  canMove?: boolean;
  onMove?: (dir: "left" | "right") => void;
  readOnly?: boolean;
}

function ColHead({ col, doRefresh, openDrawer, sort, setSort, filter, setFilter, canMove, onMove, readOnly, onHide }: ColHeadProps) {
  const ai = col.kind === "code" || col.kind === "analysis";
  const [filterOpen, setFilterOpen] = useState(false);
  const sorted = sort?.colId === col.id ? sort.dir : 0;
  const filtered = filterActive(filter);
  // Controls live in an absolutely positioned strip: rendered inline they would add
  // their width to every column FOREVER, even while invisible.
  return (
    <div className="relative group min-w-0 pr-1">
      <div className="flex items-start gap-1 flex-wrap min-w-0">
        <button
          className={`text-left break-words leading-tight min-w-0 ${ai ? "hover:text-[var(--color-primary)] uppercase" : "uppercase cursor-default"}`}
          onClick={ai ? () => openDrawer({ type: "column", colId: col.id }) : undefined}
          title={ai ? `${col.label} — szczegóły kolumny` : col.label}
        >
          {col.label}
        </button>
        <KindBadge kind={col.kind} />
        {(sorted !== 0 || filtered) && (
          <span className="inline-flex items-center gap-0.5 text-[var(--color-primary)]" title="Aktywne sortowanie/filtr">
            {sorted === 1 ? <ArrowUp size={9} /> : sorted === -1 ? <ArrowDown size={9} /> : null}
            {filtered ? <Filter size={9} /> : null}
          </span>
        )}
      </div>
      <span className="absolute -top-1 right-2.5 hidden group-hover:flex items-center gap-0.5 bg-[var(--color-bg-elevated)] border border-[var(--color-border)] px-0.5 py-0.5 z-20 shadow-sm">
        {setSort && (
          <button
            className={`p-0.5 ${sorted ? "text-[var(--color-primary)]" : "text-[var(--color-text-muted)]"} hover:text-[var(--color-primary)]`}
            title="Sortuj po tej kolumnie (rosnąco → malejąco → wyłącz)"
            onClick={() =>
              setSort(sorted === 0 ? { colId: col.id, dir: 1 }
                : sorted === 1 ? { colId: col.id, dir: -1 } : null)
            }
          >
            {sorted === 1 ? <ArrowUp size={11} /> : sorted === -1 ? <ArrowDown size={11} /> : <ArrowUpDown size={11} />}
          </button>
        )}
        {setFilter && (
          <button
            className={`p-0.5 ${filtered ? "text-[var(--color-primary)]" : "text-[var(--color-text-muted)]"} hover:text-[var(--color-primary)]`}
            title="Filtruj po tej kolumnie"
            onClick={() => setFilterOpen((v) => !v)}
          >
            <Filter size={11} />
          </button>
        )}
        {canMove && onMove && (
          <>
            <button className="p-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
              title="Przesuń kolumnę w lewo" onClick={() => onMove("left")}>
              <ChevronLeft size={11} />
            </button>
            <button className="p-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
              title="Przesuń kolumnę w prawo" onClick={() => onMove("right")}>
              <ChevronRight size={11} />
            </button>
          </>
        )}
        {onHide && (
          <button
            className="p-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
            title="Ukryj kolumnę (dane zostają)"
            onClick={onHide}
          >
            <EyeOff size={11} />
          </button>
        )}
        {ai && !readOnly && (
          <>
            <button
              className="p-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
              title="Odśwież kolumnę"
              onClick={() => doRefresh({ scope: "column", column_id: col.id })}
            >
              <RefreshCw size={11} />
            </button>
            <button
              className="p-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
              title="Szczegóły kolumny"
              onClick={() => openDrawer({ type: "column", colId: col.id })}
            >
              <MoreHorizontal size={11} />
            </button>
          </>
        )}
      </span>
      {filterOpen && setFilter && (
        <FilterPopover
          fmt={col.fmt}
          filter={filter ?? {}}
          onChange={setFilter}
          onClose={() => setFilterOpen(false)}
        />
      )}
    </div>
  );
}

/** Model's audit of its own trial run — the headline next to the sample it judged.
 * The reasoning itself belongs in the conversation (`showProblem` is for old drafts
 * that predate the thread), not in a wall of the model's raw notes. */
function ReviewNote({ review, showProblem = false }: {
  review: NonNullable<AiColumnPlan["review"]>; showProblem?: boolean;
}) {
  if (!review?.verdict) return null;
  const dead = review.verdict === "unanswerable";
  const doubt = review.verdict === "fix" && !review.repaired;
  const cls = dead
    ? "bg-red-50 border-red-500 text-red-800"
    : doubt
      ? "bg-amber-50 border-amber-400 text-amber-800"
      : "bg-emerald-50 border-emerald-500 text-emerald-800";
  return (
    <div className={`mt-2 text-[0.7rem] p-2.5 border-l-[3px] ${cls}`}>
      <b>
        {dead
          ? "Samokontrola ARON-a: mam wątpliwość do tego wyniku"
          : doubt
            ? "Samokontrola ARON-a: wynik budzi wątpliwość"
            : review.repaired
              ? "Samokontrola ARON-a: znalazł błąd i poprawił skrypt"
              : "Samokontrola ARON-a: wynik potwierdzony"}
      </b>
      {showProblem && review.problem
        ? <span className="block mt-0.5">{review.problem}</span>
        : null}
      {dead && (
        <span className="block mt-1 font-mono text-[0.62rem]">
          To ostrzeżenie, nie blokada — odpisz ARON-owi w rozmowie albo zastosuj
          kolumnę mimo niego (decyzja należy do Ciebie).
        </span>
      )}
    </div>
  );
}

/** Tiny per-column filter panel — inputs typed by the column format. */
function FilterPopover({ fmt, filter, onChange, onClose }: {
  fmt: AiSheetColumn["fmt"]; filter: ColFilter;
  onChange: (f: ColFilter | null) => void; onClose: () => void;
}) {
  const numeric = ["number", "money", "percent"].includes(fmt);
  const picks = fmt === "level"
    ? [["low", "niskie"], ["mid", "średnie"], ["high", "wysokie"]]
    : fmt === "flag" ? [["tak", "tak"], ["nie", "nie"]] : null;
  return (
    <div
      className="absolute left-0 top-full mt-1 z-30 w-52 card border-2 border-[var(--color-text-primary)] p-2.5 shadow-lg normal-case tracking-normal font-sans"
      onClick={(e) => e.stopPropagation()}
    >
      {numeric ? (
        <div className="flex items-center gap-1.5">
          <input className="input-base w-full" placeholder="od" inputMode="decimal"
            value={filter.min ?? ""} autoFocus
            onChange={(e) => onChange({ ...filter, min: e.target.value })} />
          <span className="text-[var(--color-text-muted)]">–</span>
          <input className="input-base w-full" placeholder="do" inputMode="decimal"
            value={filter.max ?? ""}
            onChange={(e) => onChange({ ...filter, max: e.target.value })} />
        </div>
      ) : picks ? (
        <select className="input-base w-full" value={filter.pick ?? ""} autoFocus
          onChange={(e) => onChange({ ...filter, pick: e.target.value || undefined })}>
          <option value="">— wszystkie —</option>
          {picks.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      ) : (
        <input className="input-base w-full" placeholder="zawiera…" autoFocus
          value={filter.text ?? ""}
          onChange={(e) => onChange({ ...filter, text: e.target.value })} />
      )}
      <div className="flex justify-between mt-2">
        <button className="font-mono text-[0.62rem] uppercase text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
          onClick={() => { onChange(null); onClose(); }}>
          Wyczyść
        </button>
        <button className="font-mono text-[0.62rem] uppercase font-bold hover:text-[var(--color-primary)]" onClick={onClose}>
          OK
        </button>
      </div>
    </div>
  );
}

function SystemCell({ data, row, col }: { data: AiSheetData | null; row: AiSheetRow; col: AiSheetColumn }) {
  const sv = data?.system_values?.[row.id]?.[col.config.field ?? ""];
  if (sv?.no_access) {
    return (
      <span className="inline-flex items-center gap-1 font-mono text-[0.66rem] text-[var(--color-text-muted)]">
        <Lock size={11} /> brak dostępu
      </span>
    );
  }
  if (!sv || sv.value === null || sv.value === undefined) {
    return <span className="text-[var(--color-text-muted)]">—</span>;
  }
  if (col.fmt === "date" && typeof sv.value === "string") {
    const days = daysUntil(sv.value);
    const tone = deadlineTone(days);
    const toneCls = tone === "red" ? "text-red-700" : tone === "amber" ? "text-amber-600" : "text-emerald-600";
    return (
      <div className="flex flex-col items-end">
        <span className="font-mono tabular-nums">{fmtDatePL(sv.value)}</span>
        <span className={`font-mono text-[0.62rem] font-semibold ${toneCls}`}>
          {days < 0 ? `${-days} dni po terminie` : `za ${days} dni`}
        </span>
      </div>
    );
  }
  const numeric = ["number", "money", "percent"].includes(col.fmt);
  return (
    <div className="flex flex-col">
      <span className={`break-words ${numeric ? "font-mono tabular-nums text-right font-semibold" : ""}`}>
        {fmtCellValue(col.fmt, { value: sv.value })}
      </span>
      {sv.detail && (
        <span className={`text-[0.66rem] text-[var(--color-text-muted)] ${numeric ? "text-right font-mono" : ""}`}>
          {sv.detail}
        </span>
      )}
    </div>
  );
}

function NormalGrid({ sheet, rows, columns, data, mut, doRefresh, openDrawer, readOnly, sort, setSort, colFilters, setColFilters, widths, setWidth, aggs, setAgg, onHideColumn, onHideRow, onMoveError }: {
  sheet: AiSheet; rows: AiSheetRow[]; columns: AiSheetColumn[]; data: AiSheetData | null;
  mut: Mut; doRefresh: (v: any) => void; openDrawer: (d: DrawerState) => void;
  readOnly: boolean;
  sort: { colId: string; dir: 1 | -1 } | null;
  setSort: (s: { colId: string; dir: 1 | -1 } | null) => void;
  colFilters: Record<string, ColFilter>;
  setColFilters: React.Dispatch<React.SetStateAction<Record<string, ColFilter>>>;
  widths: Record<string, number>;
  setWidth: (colId: string, px: number | null, persist: boolean) => void;
  aggs: Record<string, AggKind>;
  setAgg: (colId: string, kind: AggKind) => void;
  onHideColumn: (colId: string) => void;
  onHideRow: (rowId: string) => void;
  onMoveError: (msg: string) => void;
}) {
  const anyPartial = { current: false };
  const viewAltered = sort !== null || Object.values(colFilters).some(filterActive);
  const rowColWidth = widths.__row__ ?? 240;
  const totalWidth = LP_WIDTH + rowColWidth
    + columns.reduce((sum, c) => sum + (widths[c.id] ?? colWidth(c)), 0);
  const setColFilter = (colId: string) => (f: ColFilter | null) =>
    setColFilters((prev) => {
      const next = { ...prev };
      if (f === null) delete next[colId];
      else next[colId] = f;
      return next;
    });
  return (
    <table
      className="text-xs border-collapse"
      style={{ tableLayout: "fixed", width: totalWidth, minWidth: "100%" }}
    >
      {/* colgroup, not per-cell widths: with table-layout:fixed these are the
          authority, so a column really shrinks to what the user dragged. */}
      <colgroup>
        {/* Lp. — ordinal of the row AS SHOWN (after filters/sort), so "trzeci od góry"
            means the same thing to two people looking at the same screen. */}
        <col style={{ width: LP_WIDTH }} />
        <col style={{ width: rowColWidth }} />
        {columns.map((c) => (
          <col key={c.id} style={{ width: widths[c.id] ?? colWidth(c) }} />
        ))}
        {/* Spacer: leftover width lands here instead of being spread over every
            column — otherwise squeezing one column silently fattens the rest. */}
        <col />
      </colgroup>
      <thead>
        <tr>
          <th className={`${thCls} sticky left-0 z-10 bg-[var(--color-bg-elevated)] text-right`}>
            Lp.
          </th>
          <th
            className={`${thCls} ${stickyCls} bg-[var(--color-bg-elevated)] relative`}
            style={{ left: LP_WIDTH }}
          >
            {sheet.row_dimension_label}
            <ColResizer
              width={widths.__row__ ?? 240}
              onResize={(px, done) => setWidth("__row__", px, done)}
              onReset={() => setWidth("__row__", null, true)}
            />
          </th>
          {columns.map((c) => (
            <th
              key={c.id}
              className={`${thCls} overflow-visible relative`}
            >
              <ColHead
                col={c} doRefresh={doRefresh} openDrawer={openDrawer}
                sort={sort} setSort={setSort}
                filter={colFilters[c.id]} setFilter={setColFilter(c.id)}
                canMove={!readOnly} readOnly={readOnly}
                onHide={() => onHideColumn(c.id)}
                onMove={(dir) => mut.moveColumn.mutate(
                  { columnId: c.id, direction: dir },
                  { onError: (e) => onMoveError(apiErrText(e)) },
                )}
              />
              <ColResizer
                width={widths[c.id] ?? colWidth(c)}
                onResize={(px, done) => setWidth(c.id, px, done)}
                onReset={() => setWidth(c.id, null, true)}
              />
            </th>
          ))}
          <th className={thCls} aria-hidden="true" />
        </tr>
      </thead>
      <tbody>
        {rows.map((r, idx) => (
          <tr key={r.id} className={`border-t border-[var(--color-border-muted)] hover:bg-[#f3f3f1] ${r.archived ? "bg-[#f4f4f4]" : ""}`}>
            <td className="px-2 py-1.5 align-top sticky left-0 z-10 bg-[var(--color-bg-surface)] font-mono text-[0.62rem] text-[var(--color-text-muted)] text-right">
              {idx + 1}
            </td>
            <td className={`px-2 py-1.5 align-top bg-[var(--color-bg-surface)] overflow-hidden ${stickyCls}`}
                style={{ left: LP_WIDTH }}>
              <RowHead row={r} mut={mut} doRefresh={doRefresh} readOnly={readOnly}
                       canMove={!readOnly && !viewAltered}
                       onHide={() => onHideRow(r.id)} />
            </td>
            {columns.map((c) => (
              <td
                key={c.id}
                className={`px-2 py-1.5 align-top border-l border-[var(--color-border-muted)] overflow-hidden ${c.kind === "code" || c.kind === "analysis" ? "cursor-pointer hover:outline hover:outline-1 hover:outline-[var(--color-border-strong)] -outline-offset-1" : ""}`}
                onClick={c.kind === "code" || c.kind === "analysis"
                  ? () => openDrawer({ type: "cell", rowId: r.id, colId: c.id })
                  : undefined}
              >
                {c.kind === "system" ? (
                  <SystemCell data={data} row={r} col={c} />
                ) : c.kind === "manual" ? (
                  <ManualCell
                    col={c} cell={cellFor(data, r.id, c.id)}
                    onSave={(v) => mut.setCell.mutate({ row_id: r.id, column_id: c.id, value: v })}
                  />
                ) : (
                  <CellContent col={c} cell={cellFor(data, r.id, c.id)} />
                )}
              </td>
            ))}
            <td className="bg-[var(--color-bg-surface)]" aria-hidden="true" />
          </tr>
        ))}
        {rows.length === 0 && (
          <tr>
            <td colSpan={columns.length + 3} className="px-3 py-6 text-center text-[var(--color-text-muted)]">
              Brak wierszy — dodaj przez „Aktualizuj wiersze” albo „+ wiersz”.
            </td>
          </tr>
        )}
      </tbody>
      {rows.length > 0 && (
        <tfoot>
          <tr className="border-t border-[var(--color-border-strong)]">
            <td className="px-2 py-1.5 bg-[var(--color-bg-elevated)] sticky left-0 z-10" aria-hidden="true" />
            <td className={`px-2 py-1.5 font-mono text-[0.64rem] font-bold text-[var(--color-text-secondary)] bg-[var(--color-bg-elevated)] ${stickyCls}`}
                style={{ left: LP_WIDTH }}>
              PODSUMOWANIE ({rows.length})
            </td>
            {columns.map((c) => {
              const kind = aggs[c.id] ?? defaultAgg(c.fmt);
              const cells = c.kind === "system"
                ? rows.map((r): AiSheetCell | undefined => {
                    const sv = data?.system_values?.[r.id]?.[c.config.field ?? ""];
                    if (!sv) return undefined;
                    return {
                      id: "", value: { value: sv.value }, previous: null,
                      status: sv.no_access ? "no_access" : "ok", error: "",
                      sources: [], computed_at: null, computed_by: "", cost_usd: "0",
                    };
                  })
                : rows.map((r) => cellFor(data, r.id, c.id));
              const agg = computeAgg(kind, c.fmt, cells);
              if (agg?.partial) anyPartial.current = true;
              const opts = aggOptions(c.fmt);
              return (
                <td key={c.id} className="px-1.5 py-1 bg-[var(--color-bg-elevated)] border-l border-[var(--color-border-muted)] align-bottom group/agg"
                    title={agg?.partial ? "Wynik z pominięciem komórek bez dostępu" : undefined}>
                  <div className="flex flex-col items-end gap-0.5">
                    <span className="font-mono text-[0.68rem] font-bold text-[var(--color-text-primary)]">
                      {agg ? `${agg.text}${agg.partial ? "*" : ""}` : "—"}
                    </span>
                    {/* Per-column footer function: a director wants a sum here and a
                        median there, on the same sheet. */}
                    <select
                      className="w-full max-w-full bg-transparent border border-transparent hover:border-[var(--color-border)] font-mono text-[0.58rem] uppercase text-[var(--color-text-muted)] cursor-pointer text-right py-0 px-0.5"
                      value={kind}
                      title="Podsumowanie tej kolumny"
                      onChange={(e) => setAgg(c.id, e.target.value as AggKind)}
                    >
                      {opts.map((o) => (
                        <option key={o} value={o}>{AGG_LABEL[o]}</option>
                      ))}
                    </select>
                  </div>
                </td>
              );
            })}
            <td className="bg-[var(--color-bg-elevated)]" aria-hidden="true" />
          </tr>
          {anyPartial.current && (
            <tr>
              <td colSpan={columns.length + 3} className="px-2 py-1 font-mono text-[0.6rem] text-[var(--color-text-muted)] bg-[var(--color-bg-elevated)]">
                * wynik z pominięciem wierszy bez dostępu do kwot
              </td>
            </tr>
          )}
        </tfoot>
      )}
    </table>
  );
}

function TransposedGrid({ sheet, rows, columns, data, doRefresh, openDrawer, readOnly }: {
  sheet: AiSheet; rows: AiSheetRow[]; columns: AiSheetColumn[]; data: AiSheetData | null;
  doRefresh: (v: any) => void; openDrawer: (d: DrawerState) => void; readOnly: boolean;
}) {
  return (
    <>
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr>
            <th className={`${thCls} ${stickyCls} bg-[var(--color-bg-elevated)] min-w-[180px]`}>
              Atrybut ↔ {sheet.row_dimension_label}
            </th>
            {rows.map((r) => (
              <th key={r.id} className={`${thCls}`} style={{ minWidth: 190 }}>
                <div className="flex items-start gap-1 group">
                  <div className="whitespace-normal leading-tight">
                    {r.label}
                    <div className="font-normal normal-case tracking-normal text-[0.62rem]">{r.sublabel}</div>
                  </div>
                  {!readOnly && (
                    <button
                      className="p-0.5 opacity-0 group-hover:opacity-100 hover:text-[var(--color-primary)]"
                      title="Odśwież (kolumny AI tego obiektu)"
                      onClick={() => doRefresh({ scope: "row", row_id: r.id })}
                    >
                      <RefreshCw size={10} />
                    </button>
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {columns.map((c) => (
            <tr key={c.id} className="border-t border-[var(--color-border-muted)] hover:bg-[#f3f3f1]">
              <td className={`px-2.5 py-1.5 align-top bg-[var(--color-bg-surface)] ${stickyCls} overflow-visible`}>
                <ColHead col={c} doRefresh={doRefresh} openDrawer={openDrawer} readOnly={readOnly} />
              </td>
              {rows.map((r) => (
                <td
                  key={r.id}
                  className={`px-2.5 py-1.5 align-top border-l border-[var(--color-border-muted)] ${c.kind !== "system" ? "cursor-pointer" : ""}`}
                  onClick={c.kind === "code" || c.kind === "analysis"
                  ? () => openDrawer({ type: "cell", rowId: r.id, colId: c.id })
                  : undefined}
                >
                  {c.kind === "system" ? (
                    <SystemCell data={data} row={r} col={c} />
                  ) : c.kind === "manual" ? (
                    <span className="break-words">{fmtCellValue(c.fmt, cellFor(data, r.id, c.id)?.value ?? null)}</span>
                  ) : (
                    <CellContent col={c} cell={cellFor(data, r.id, c.id)} />
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="px-2.5 py-1.5 font-mono text-[0.62rem] text-[var(--color-text-muted)]">
        Widok obrócony o 90° — sumy, sortowanie, filtry i strzałki kolejności wracają w układzie zwykłym.
      </div>
    </>
  );
}

const colWidth = (c: AiSheetColumn): number => DEFAULT_COL_WIDTH[c.fmt] ?? 160;

/** Drag handle on a column edge. Live-resizes while dragging, persists on release;
 * double-click restores the default width for that column's format. */
function ColResizer({ width, onResize, onReset }: {
  width: number; onResize: (px: number, done: boolean) => void; onReset: () => void;
}) {
  const start = useRef<{ x: number; w: number } | null>(null);
  useEffect(() => {
    const move = (e: MouseEvent) => {
      if (!start.current) return;
      onResize(clampWidth(start.current.w + e.clientX - start.current.x), false);
    };
    const up = (e: MouseEvent) => {
      if (!start.current) return;
      onResize(clampWidth(start.current.w + e.clientX - start.current.x), true);
      start.current = null;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
  }, [onResize]);
  return (
    <span
      role="separator"
      aria-orientation="vertical"
      title="Przeciągnij, żeby zmienić szerokość · dwuklik = domyślna"
      className="absolute top-0 right-0 h-full w-[9px] z-30 cursor-col-resize select-none hover:bg-[var(--color-primary)]/25"
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
        start.current = { x: e.clientX, w: width };
        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";
      }}
      onDoubleClick={(e) => { e.stopPropagation(); onReset(); }}
    />
  );
}

/* ── column conversation ───────────────────────────────────────────────────── */

/** ARON is told to write plain prose — but a model is a model, so whatever markup
 * slips through gets RENDERED instead of landing on the page as ### and **
 * (2026-09-01: the self-review showed up as a red wall of raw markdown). */
function ChatMarkdown({ text }: { text: string }) {
  return (
    <div className="text-[0.74rem] leading-relaxed break-words [&_p]:my-1 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0 [&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:my-1 [&_ol]:list-decimal [&_ol]:pl-4 [&_li]:my-0.5 [&_strong]:font-bold [&_h1]:font-bold [&_h2]:font-bold [&_h3]:font-bold [&_h1]:text-[0.78rem] [&_h2]:text-[0.78rem] [&_h3]:text-[0.76rem] [&_hr]:my-2 [&_hr]:border-[var(--color-border-muted)] [&_code]:font-mono [&_code]:bg-black/[0.06] [&_code]:px-1 [&_pre]:bg-[#161616] [&_pre]:text-[#e6e6e6] [&_pre]:p-2 [&_pre]:my-1.5 [&_pre]:overflow-x-auto [&_pre]:text-[0.64rem] [&_a]:underline">
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
    </div>
  );
}

const CHAT_TIME = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" });
};

function ChatBubble({ m }: { m: AiChatMessage }) {
  const mine = m.role === "user";
  const asks = m.kind === "question";
  return (
    <div className={mine ? "flex justify-end" : "flex justify-start"}>
      <div className={`max-w-[88%] border-l-[3px] p-2 ${
        mine
          ? "border-[var(--color-border-strong)] bg-[var(--color-bg-surface)]"
          : asks
            ? "border-amber-400 bg-amber-50"
            : "border-[var(--color-primary)] bg-[var(--color-bg-surface)]"
      }`}>
        <div className="flex items-baseline gap-2 font-mono text-[0.58rem] uppercase text-[var(--color-text-muted)]">
          <span className="font-bold">{mine ? "Ty" : "ARON"}</span>
          {m.kind === "spec" && <span>opis kolumny</span>}
          {asks && <span className="text-amber-700">pytanie do Ciebie</span>}
          {m.at && <span>{CHAT_TIME(m.at)}</span>}
          {Number(m.cost_usd ?? 0) > 0 && <span>${Number(m.cost_usd).toFixed(3)}</span>}
        </div>
        {mine ? (
          <p className="mt-0.5 text-[0.74rem] whitespace-pre-wrap">{m.text}</p>
        ) : (
          <div className="mt-0.5"><ChatMarkdown text={m.text} /></div>
        )}
      </div>
    </div>
  );
}

/**
 * The column conversation. A spec typed into one box rarely lands on a number a
 * director trusts, so the draft is argued out here: ARON says what it counted and
 * what it doubts, the human answers, and only then does the column get applied.
 * ARON decides per message whether to just reply (cents) or rebuild the column
 * (new script + trial on the sample rows + self-review).
 */
function ColumnChat({ col, mut, readOnly, running, logText, onSent, setErr }: {
  col: AiSheetColumn;
  mut: Mut;
  readOnly: boolean;
  running: boolean;
  logText: string;
  onSent: (r: { task_id: string; column_id: string }) => void;
  setErr: (m: string) => void;
}) {
  const [text, setText] = useState("");
  const endRef = useRef<HTMLDivElement | null>(null);
  const thread = col.chat ?? [];
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [thread.length, logText]);
  const send = () => {
    const message = text.trim();
    if (!message || running) return;
    mut.chatColumn.mutate({ colId: col.id, message }, {
      onSuccess: (r) => { setText(""); onSent(r); },
      onError: (e) => setErr(apiErrText(e)),
    });
  };
  const logLines = logText.split("\n").filter(Boolean);
  return (
    <div className="mt-1.5 border border-[var(--color-border)]">
      <div className="max-h-[38vh] overflow-y-auto p-2.5 space-y-2 bg-[var(--color-bg-elevated)]">
        {thread.length === 0 && !running && (
          <p className="text-[0.7rem] text-[var(--color-text-muted)]">
            Ta kolumna powstała przed rozmową — napisz, co poprawić, a ARON odpowie
            i w razie potrzeby przeliczy ją od nowa.
          </p>
        )}
        {thread.map((m, i) => <ChatBubble key={`${m.at}-${i}`} m={m} />)}
        {running && (
          <div className="border-l-[3px] border-[var(--color-primary)] bg-[var(--color-bg-surface)] p-2">
            <div className="flex items-center gap-2 font-mono text-[0.58rem] uppercase text-[var(--color-text-muted)]">
              <Spinner size="sm" /> ARON pracuje…
            </div>
            <div className="mt-1 max-h-32 overflow-y-auto font-mono text-[0.62rem] leading-relaxed text-[var(--color-text-secondary)] whitespace-pre-wrap">
              {logLines.length ? logLines.slice(-12).join("\n") : "Startuję…"}
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>
      {!readOnly && (
        <div className="border-t border-[var(--color-border)] p-2">
          <textarea
            className="input-base w-full min-h-[52px] text-xs"
            value={text}
            disabled={running}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); send(); }
            }}
            placeholder="Napisz, co doprecyzować — np. „licz z korytarza, nie z długości trasy”, albo zapytaj, skąd ta liczba…"
          />
          <div className="flex items-center gap-2 mt-1">
            <Button size="sm" loading={mut.chatColumn.isPending} disabled={running || !text.trim()} onClick={send}>
              <Send size={12} className="mr-1" /> Wyślij
            </Button>
            <span className="font-mono text-[0.6rem] text-[var(--color-text-muted)]">
              Ctrl+Enter wysyła · pytanie kosztuje grosze, zmiana sposobu liczenia =
              nowy skrypt i próba na wierszach
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── drawers: column / cell / info ─────────────────────────────────────────── */

function ColumnDrawer({ sheet, colId, data, mut, startTask, setBanner, onOpenColumn, onClose }: {
  sheet: AiSheet; colId: string; data: AiSheetData | null; mut: Mut;
  startTask: (id: string, kind: "refresh" | "plan") => void;
  setBanner: (b: React.ReactNode | null) => void;
  onOpenColumn: (colId: string) => void;
  onClose: () => void;
}) {
  const col = (data?.sheet.columns ?? sheet.columns).find((c) => c.id === colId);
  // A conversation turn runs in the background like planning does; the log lands
  // inside the thread so the drawer never looks frozen.
  const [chatTaskId, setChatTaskId] = useState<string | null>(null);
  const chatTask = useTaskPolling(chatTaskId);
  useEffect(() => {
    const st = chatTask.data?.status;
    if (!st || !["success", "failure", "cancelled"].includes(st)) return;
    setChatTaskId(null);
    mut.invalidate();
    if (st !== "success") {
      setBanner(
        <span className="text-red-700">
          Rozmowa o kolumnie nie powiodła się: {chatTask.data?.error || "nieznany błąd"}
        </span>,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatTask.data?.status]);
  if (!col) return null;
  const plan = col.plan ?? {};
  const isAi = col.kind !== "system";
  const readOnly = !sheet.is_owner;
  const allFailed = col.kind === "code" && (plan.sample?.length ?? 0) > 0
    && plan.sample!.every((x) => !x.ok);
  const doubt = plan.review?.verdict === "unanswerable";
  const dependents = (data?.sheet.columns ?? sheet.columns).filter(
    (c) => !c.is_draft && c.id !== col.id && (c.plan?.depends_on ?? []).includes(col.label),
  );
  return (
    <Drawer
      title={`Kolumna · ${col.label}${col.is_draft ? " (szkic)" : ""}`}
      onClose={onClose}
      footer={
        readOnly ? (
          <Button size="sm" variant="ghost" onClick={onClose}>Zamknij</Button>
        ) : col.is_draft ? (
          <>
            {!allFailed && (
              <Button size="sm" variant={doubt ? "secondary" : undefined}
                loading={mut.applyColumn.isPending} onClick={() =>
                  mut.applyColumn.mutate({ colId: col.id, force: doubt }, {
                    onSuccess: (r) => { startTask(r.task_id, "refresh"); onClose(); },
                  })
                }>
                {doubt ? "Zastosuj mimo ostrzeżenia" : "Zastosuj i policz wiersze"}
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => { mut.discardColumn.mutate(col.id); onClose(); }}>
              Odrzuć szkic
            </Button>
          </>
        ) : isAi ? (
          <>
            <Button size="sm" onClick={() => {
              mut.refresh.mutate({ scope: "column", column_id: col.id }, {
                onSuccess: (r) => { startTask(r.task_id, "refresh"); onClose(); },
              });
            }}>
              <RefreshCw size={12} className="mr-1" /> Odśwież kolumnę
            </Button>
            <Button size="sm" variant="ghost" onClick={() => {
              if (!window.confirm("Usunąć kolumnę i jej wyniki?")) return;
              mut.deleteColumn.mutate(col.id, {
                onError: (e) => setBanner(<span className="text-red-700">{apiErrText(e)}</span>),
              });
              onClose();
            }}>
              <Trash2 size={12} className="mr-1" /> Usuń
            </Button>
          </>
        ) : (
          <Button size="sm" variant="ghost" onClick={() => { mut.deleteColumn.mutate(col.id); onClose(); }}>
            <Trash2 size={12} className="mr-1" /> Usuń kolumnę
          </Button>
        )
      }
    >
      <div className="flex items-center gap-2">
        <KindBadge kind={col.kind} />
        <span className="font-mono text-[0.66rem] text-[var(--color-text-muted)]">
          {col.kind === "code" && `utworzenie: ~${fmtUsd(plan.cost_usd ?? 0)} · odświeżanie: $0`}
          {col.kind === "analysis" && `≈ ${fmtUsd(col.cost_per_cell)}/komórkę przy każdym odświeżeniu`}
          {col.kind === "system" && "wartość liczona na żywo z portfela"}
        </span>
      </div>
      {col.kind === "code" && (
        <div className="mt-2 text-[0.72rem] text-[var(--color-text-secondary)] bg-[var(--color-bg-elevated)] border-l-[3px] border-[var(--color-border-strong)] p-2.5 leading-relaxed">
          Tryb <b>KOD</b> — skrypt zapisany przy utworzeniu, uruchamiany w piaskownicy Warsztatu
          (tylko odczyt, Twoje uprawnienia). Odświeżanie: <b>bez tokenów</b>.
        </div>
      )}
      {col.kind === "analysis" && (
        <div className="mt-2 text-[0.72rem] text-[var(--color-text-secondary)] bg-[var(--color-bg-elevated)] border-l-[3px] border-[var(--color-border-strong)] p-2.5 leading-relaxed">
          Tryb <b>ANALIZA</b> — każda komórka to osobna analiza AI (limit narzędzi na komórkę),
          wynik z uzasadnieniem i cytowaniami.
        </div>
      )}
      {isAi && (
        <>
          {allFailed && plan.review?.verdict !== "unanswerable" && (
            <div className="mt-2 text-xs bg-red-50 border-l-[3px] border-red-400 text-red-800 p-2.5">
              Próba tego skryptu <b>nie przeszła na żadnym wierszu</b> (mimo poprawek).
              Nie da się go zastosować — napisz ARON-owi w rozmowie, czego brakuje,
              albo odrzuć szkic. Szczegóły błędów w tabeli próby poniżej.
            </div>
          )}
          <SectionLabel>Rozmowa o kolumnie</SectionLabel>
          <ColumnChat
            col={col}
            mut={mut}
            readOnly={readOnly}
            running={!!chatTaskId}
            logText={chatTask.data?.log ?? ""}
            onSent={(r) => {
              setChatTaskId(r.task_id);
              // On an APPLIED column the server opens a draft that will replace it —
              // follow it, otherwise the answer lands on a column nobody is looking at.
              if (r.column_id !== col.id) onOpenColumn(r.column_id);
            }}
            setErr={(m) => setBanner(<span className="text-red-700">{m}</span>)}
          />
          {!col.is_draft && !readOnly && (
            <p className="font-mono text-[0.62rem] text-[var(--color-text-muted)] mt-1">
              Kolumna jest już policzona — nowa wersja powstaje jako szkic i podmieni
              ją dopiero, gdy klikniesz „Zastosuj”.
            </p>
          )}
          <SectionLabel>Dlaczego ten tryb</SectionLabel>
          <p className="text-xs">{plan.reason || "—"}</p>
          {plan.challenged && (
            <p className="text-[0.66rem] text-[var(--color-text-muted)] mt-1">
              Dopytaliśmy, czy nie da się tego policzyć skryptem (byłoby darmowe przy
              odświeżaniu) — powyższe to odpowiedź ARON-a.
            </p>
          )}
          {plan.repairs ? (
            <p className="text-[0.66rem] font-mono text-[var(--color-text-muted)] mt-1">
              poprawki kodu po próbie: {plan.repairs}
            </p>
          ) : null}
          {col.code && (
            <>
              <SectionLabel>Wygenerowany skrypt</SectionLabel>
              <PythonCodeBlock code={col.code} theme="monokai" fontSize="0.66rem" />
            </>
          )}
          {(plan.sample?.length ?? 0) > 0 && (
            <>
              <SectionLabel>Próba na wierszach</SectionLabel>
              <SampleTable sample={plan.sample!} />
              {plan.review && (
                <ReviewNote review={plan.review} showProblem={(col.chat?.length ?? 0) === 0} />
              )}
              <p className="font-mono text-[0.62rem] text-[var(--color-text-muted)] mt-1">
                Sprawdź, czy wartości mają sens — dopiero „Zastosuj” liczy całość.
              </p>
            </>
          )}
          {((plan.depends_on?.length ?? 0) > 0 || dependents.length > 0) && (
            <>
              <SectionLabel>Zależności</SectionLabel>
              {(plan.depends_on?.length ?? 0) > 0 && (
                <p className="text-xs">
                  Korzysta z:{" "}
                  {plan.depends_on!.map((d) => (
                    <span key={d} className="inline-flex px-1.5 mr-1 border border-[var(--color-border-strong)] bg-[var(--color-bg-elevated)] font-mono text-[0.62rem]">{d}</span>
                  ))}
                  <span className="block mt-1 text-[0.66rem] text-[var(--color-text-muted)]">
                    Musi stać na prawo od tych kolumn — kolejności łamiącej zależność
                    nie da się ustawić.
                  </span>
                </p>
              )}
              {dependents.length > 0 && (
                <p className="text-xs mt-1">
                  Używana przez:{" "}
                  {dependents.map((d) => (
                    <span key={d.id} className="inline-flex px-1.5 mr-1 border border-[var(--color-border-strong)] bg-[var(--color-bg-elevated)] font-mono text-[0.62rem]">{d.label}</span>
                  ))}
                  <span className="block mt-1 text-[0.66rem] text-[var(--color-text-muted)]">
                    Dopóki tamte istnieją, tej kolumny nie można usunąć, przemianować
                    ani przestawić za nie.
                  </span>
                </p>
              )}
            </>
          )}
          <SectionLabel>Skąd dane</SectionLabel>
          <SourceList sources={plan.sources ?? []} />
        </>
      )}
    </Drawer>
  );
}

function CellDrawer({ data, rowId, colId, mut, startTask, onClose }: {
  data: AiSheetData; rowId: string; colId: string; mut: Mut;
  startTask: (id: string, kind: "refresh" | "plan") => void; onClose: () => void;
}) {
  const row = data.rows.find((r) => r.id === rowId);
  const col = data.sheet.columns.find((c) => c.id === colId);
  const cell = cellFor(data, rowId, colId);
  if (!row || !col) return null;
  const was = cell ? previousLabel(col.fmt, cell.previous) : null;
  const stale = cell ? isStale(cell.computed_at) : false;
  return (
    <Drawer
      title={col.label}
      onClose={onClose}
      footer={
        <>
          {data.sheet.is_owner && (
            <Button size="sm" onClick={() =>
              mut.refresh.mutate({ scope: "cell", row_id: rowId, column_id: colId }, {
                onSuccess: (r) => { startTask(r.task_id, "refresh"); onClose(); },
              })
            }>
              <RefreshCw size={12} className="mr-1" /> Odśwież komórkę
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={onClose}>Zamknij</Button>
        </>
      }
    >
      <div className="font-mono text-[0.66rem] text-[var(--color-text-muted)]">{row.label} · {row.sublabel}</div>
      <div className="mt-2 text-xl font-mono font-bold">
        {cell?.status === "no_access" ? (
          <span className="inline-flex items-center gap-2 text-base text-[var(--color-text-muted)]"><Lock size={14} /> brak dostępu</span>
        ) : cell?.status === "error" ? (
          <span className="text-base text-red-700">błąd obliczenia</span>
        ) : (
          fmtCellValue(col.fmt, cell?.value ?? null)
        )}
      </div>
      {was && (
        <span className="inline-block mt-1 px-1.5 border border-amber-200 bg-amber-50 text-amber-700 font-mono text-[0.64rem]">
          było: <s>{was}</s>
        </span>
      )}
      {cell?.status === "no_access" && (
        <div className="mt-3 text-xs bg-amber-50 border-l-[3px] border-amber-400 text-amber-800 p-2.5">
          Arkusz liczy w <b>Twoich</b> uprawnieniach — tych danych (np. kwot tego
          przedsięwzięcia) nie masz prawa czytać, więc komórka mówi to wprost.
          Nigdy nie wstawiamy tu zera; sumy z taką komórką dostają gwiazdkę.
        </div>
      )}
      {cell?.status === "error" && cell.error && (
        <div className="mt-3 text-xs bg-red-50 border-l-[3px] border-red-400 text-red-800 p-2.5 font-mono whitespace-pre-wrap">
          {cell.error}
        </div>
      )}
      {cell?.value?.detail && (
        <>
          <SectionLabel>Uzasadnienie</SectionLabel>
          <p className="text-xs">{cell.value.detail}</p>
        </>
      )}
      <SectionLabel>Skąd to wiadomo</SectionLabel>
      <SourceList sources={cell?.sources ?? []} />
      <dl className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-1 mt-4 text-xs">
        <dt className="section-label">Tryb</dt>
        <dd><KindBadge kind={col.kind} /></dd>
        <dt className="section-label">Policzone</dt>
        <dd className="font-mono">
          {formatDateTime(cell?.computed_at ?? null)}
          {stale && (
            <span className="ml-1.5 px-1 border border-amber-200 bg-amber-50 text-amber-700 font-mono text-[0.6rem] uppercase">do odświeżenia</span>
          )}
        </dd>
        <dt className="section-label">Koszt komórki</dt>
        <dd className="font-mono">
          {col.kind === "code" ? "$0 (bez tokenów)" : fmtUsd(cell?.cost_usd ?? 0)}
        </dd>
      </dl>
    </Drawer>
  );
}

function InfoDrawer({ onClose }: { onClose: () => void }) {
  return (
    <Drawer title="Jak to działa" onClose={onClose}
      footer={<Button size="sm" variant="ghost" onClick={onClose}>Zamknij</Button>}>
      <SectionLabel>Dostęp i zakres</SectionLabel>
      <p className="text-xs">
        Składowa DLC_01, tylko dane ZE. Zakładkę widzą <b>dyrektorzy techniczni i CEO</b>;
        każdy ma własne, prywatne arkusze.
      </p>
      <SectionLabel>Wiersze</SectionLabel>
      <p className="text-xs">
        Przy tworzeniu arkusza wybierasz WYMIAR wierszy: projekt ZE, oddział ZE,
        koordynator albo podwykonawca — i filtry. Wiersze są <b>zapisane na stałe</b> razem
        z wynikami; nowe obiekty dochodzą tylko przez „Aktualizuj wiersze”.
      </p>
      <SectionLabel>Kolumny — trzy rodzaje</SectionLabel>
      <p className="text-xs"><KindBadge kind="system" /> pola portfela — zawsze aktualne, zero kosztu.</p>
      <p className="text-xs mt-1">
        <KindBadge kind="code" /> z Twojego opisu ARON pisze <b>skrypt</b> (piaskownica Warsztatu,
        wyłącznie odczyt przez API na <b>Twoich</b> uprawnieniach). Generacja raz — każde
        odświeżenie potem jest <b>bez tokenów</b> i deterministyczne. Przed zastosowaniem
        widzisz kod i próbę na 3 wierszach.
      </p>
      <p className="text-xs mt-1">
        <KindBadge kind="analysis" /> gdy opisu nie da się zamienić na kod (ocena treści,
        rekomendacja) — każda komórka to krótka analiza AI z uzasadnieniem i cytowaniami,
        płatna przy każdym odświeżeniu.
      </p>
      <SectionLabel>Uprawnienia i kwoty</SectionLabel>
      <p className="text-xs">
        Wszystko liczy się w uprawnieniach zalogowanego. Kwoty przedsięwzięcia bez grantu
        finansowego = komórka <b>„brak dostępu”</b> (nigdy zero), sumy z gwiazdką. Arkusz
        niczego nie zapisuje do bazy — tylko czyta.
      </p>
      <SectionLabel>Koszty</SectionLabel>
      <p className="text-xs">
        Utworzenie kolumny KOD: ~$0,01–0,05 jednorazowo (generacja + próba). Komórka
        ANALIZY: ~$0,003–0,02. Licznik dzienny na górze strony; osobny dzienny budżet
        arkuszy (domyślnie $15) zatrzymuje analizy, nigdy kolumn KOD.
      </p>
      <SectionLabel>Zależności między kolumnami</SectionLabel>
      <p className="text-xs">
        Kolumna liczona później widzi wartości kolumn na lewo od siebie (skrypt:
        <code className="font-mono"> ctx["row_values"]</code>; analiza dostaje je w
        kontekście) — można budować np. „% fakturowania” z „Wartości umowy” obok.
      </p>
      <SectionLabel>Udostępnianie</SectionLabel>
      <p className="text-xs">
        „Udostępnij” pokazuje arkusz wybranym dyr. technicznym / CEO <b>tylko do
        odczytu</b> (widzą Twoje wyniki jak raport). Odbiorca może „Kopiuj do siebie” —
        dostaje własną, edytowalną kopię liczoną już na jego uprawnieniach.
      </p>
      <SectionLabel>Świeżość</SectionLabel>
      <p className="text-xs">
        Wynik ma datę; starszy niż 5 dni dostaje pomarańczową kropkę. Odświeżasz komórkę,
        wiersz, kolumnę albo całość — w tle, bez blokowania pracy. Sortowanie i filtry po
        kolumnach oraz strzałki kolejności są na nagłówkach.
      </p>
    </Drawer>
  );
}

/* ── modals ────────────────────────────────────────────────────────────────── */

/**
 * Modal shell. Sized to the viewport (header/footer pinned, body scrolls) so a long
 * generated script never pushes the action buttons off-screen, and `size` widens it
 * for content that is code rather than a form. The backdrop blurs the sheet behind
 * it, and the accent shadow is one soft offset — the old 3px hard shadow read as a
 * broken red border on a box this big.
 */
function ModalShell({ title, onClose, children, footer, size = "md" }: {
  title: string; onClose: () => void; children: React.ReactNode; footer: React.ReactNode;
  size?: "md" | "lg";
}) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center bg-black/40 backdrop-blur-[3px] p-4 pt-[7vh]"
      onClick={onClose}
    >
      <div
        className={`card w-full ${size === "lg" ? "max-w-4xl" : "max-w-xl"} border-2 border-[var(--color-text-primary)] flex flex-col max-h-[86vh]`}
        style={{ boxShadow: "10px 10px 0 rgba(220,31,31,0.28)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center px-4 py-2.5 border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)] shrink-0">
          <h3 className="font-mono text-xs font-bold uppercase tracking-widest">{title}</h3>
          <span className="flex-1" />
          <button className="p-1 hover:text-[var(--color-primary)]" onClick={onClose} aria-label="Zamknij"><X size={13} /></button>
        </div>
        <div className="p-4 overflow-y-auto flex-1 min-h-0">{children}</div>
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-[var(--color-border)] bg-[var(--color-bg-elevated)] shrink-0">
          {footer}
        </div>
      </div>
    </div>
  );
}

/** Where today's money went + the knob to raise the cap. A limit you cannot see the
 * reason for, and cannot lift yourself, stops the work dead. */
function SpendModal({ spend, onClose }: { spend: SpendInfo; onClose: () => void }) {
  const setBudget = useSetBudget();
  const [value, setValue] = useState(String(Math.round(Number(spend.budget_usd))));
  const [err, setErr] = useState("");
  const plans = spend.plans ?? [];
  return (
    <ModalShell title="Koszty i limit dzienny" size="lg" onClose={onClose}
      footer={
        <>
          <Button size="sm" variant="ghost" onClick={onClose}>Zamknij</Button>
          <Button size="sm" loading={setBudget.isPending} onClick={() =>
            setBudget.mutate(value, {
              onSuccess: onClose,
              onError: (e) => setErr(apiErrText(e)),
            })
          }>
            Zapisz limit
          </Button>
        </>
      }>
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <span className="section-label">Wydane dziś</span>
          <div className="font-mono text-lg font-bold">${Number(spend.today_usd).toFixed(2)}</div>
        </div>
        <div>
          <span className="section-label">Twój dzienny limit (USD)</span>
          <input className="input-base w-28 font-mono" value={value} inputMode="decimal"
                 onChange={(e) => setValue(e.target.value)} />
        </div>
        <p className="text-[0.68rem] text-[var(--color-text-muted)] max-w-sm">
          Limit dotyczy tylko Ciebie i tylko Arkusza AI. Domyślny to
          ${Number(spend.default_budget_usd ?? 15).toFixed(0)}. Po jego wyczerpaniu
          stają wyłącznie analizy i tworzenie nowych kolumn — kolumny KOD odświeżają
          się dalej za darmo.
        </p>
      </div>
      {err && <p className="text-xs text-red-700 mt-2">{err}</p>}

      <SectionLabel>Na co poszły dzisiejsze pieniądze</SectionLabel>
      <p className="text-xs mb-1">
        Tworzenie kolumn: <b>${Number(spend.plans_usd ?? 0).toFixed(2)}</b> ·
        {" "}komórki analiz: <b>${Number(spend.cells_usd ?? 0).toFixed(2)}</b>
        {" "}({spend.cells_count ?? 0} szt.)
      </p>
      {plans.length > 0 && (
        <table className="w-full text-[0.7rem] border border-[var(--color-border)]">
          <thead>
            <tr className="bg-[var(--color-bg-elevated)]">
              <th className="text-left px-2 py-1 font-mono text-[0.6rem] uppercase">Kolumna</th>
              <th className="text-left px-2 py-1 font-mono text-[0.6rem] uppercase">Arkusz</th>
              <th className="text-right px-2 py-1 font-mono text-[0.6rem] uppercase">Koszt</th>
            </tr>
          </thead>
          <tbody>
            {plans.map((p, i) => (
              <tr key={i} className="border-t border-[var(--color-border-muted)]">
                <td className="px-2 py-1">
                  {p.label}
                  {p.repairs ? <span className="text-[var(--color-text-muted)]"> · {p.repairs} popr.</span> : null}
                </td>
                <td className="px-2 py-1 text-[var(--color-text-muted)]">{p.sheet}</td>
                <td className="px-2 py-1 text-right font-mono">${Number(p.cost_usd).toFixed(3)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="font-mono text-[0.62rem] text-[var(--color-text-muted)] mt-2">
        Koszt liczony z tokenów każdej tury (cennik modelu, apps/aron/services/pricing).
        Utworzenie kolumny to kilka tur: rozpoznanie danych, napisanie kodu, próba
        i samokontrola — dlatego jest droższe niż jedna odpowiedź czatu. Odświeżanie
        gotowej kolumny KOD nie kosztuje nic.
      </p>
    </ModalShell>
  );
}

function NewSheetModal({ options, mut, onCreated, onClose }: {
  options: ReturnType<typeof useSheetOptions>["data"];
  mut: Mut; onCreated: (s: AiSheet) => void; onClose: () => void;
}) {
  const [name, setName] = useState("");
  const [dimension, setDimension] = useState<AiSheetDimension>("project");
  const [err, setErr] = useState("");
  return (
    <ModalShell title="Nowy arkusz AI" onClose={onClose} footer={
      <>
        <Button size="sm" variant="ghost" onClick={onClose}>Anuluj</Button>
        <Button size="sm" disabled={!options} loading={mut.createSheet.isPending} onClick={() => {
          if (!name.trim()) { setErr("Podaj nazwę arkusza."); return; }
          mut.createSheet.mutate(
            { name: name.trim(), row_dimension: dimension, filters: { active_only: true } },
            { onSuccess: onCreated, onError: (e) => setErr(apiErrText(e)) },
          );
        }}>
          Utwórz i zapisz wiersze
        </Button>
      </>
    }>
      {!options ? (
        <div className="flex items-center gap-2 py-4 text-xs text-[var(--color-text-muted)]">
          <Spinner size="sm" /> Wczytuję wymiary i filtry…
        </div>
      ) : (
        <div className="space-y-3">
          <label className="block">
            <span className="section-label">Nazwa</span>
            <input className="input-base w-full" value={name} onChange={(e) => setName(e.target.value)}
                   placeholder="np. Ryzyko terminów — II półrocze" autoFocus />
          </label>
          <label className="block">
            <span className="section-label">Czym są wiersze (nie do zmiany później)</span>
            <select className="input-base w-full" value={dimension}
                    onChange={(e) => setDimension(e.target.value as AiSheetDimension)}>
              {options.dimensions.map((d) => (
                <option key={d.key} value={d.key}>{d.label}</option>
              ))}
            </select>
          </label>
          <p className="text-[0.68rem] text-[var(--color-text-muted)]">
            Arkusz od razu zapisze pasujące wiersze. Filtry (oddział, koordynator,
            szukajka) są na pasku arkusza — tam zawężasz i dodajesz wiersze.
          </p>
          {err && <p className="text-xs text-red-700">{err}</p>}
        </div>
      )}
    </ModalShell>
  );
}

/**
 * "Nowa kolumna" — the demo's three-step flow, for real:
 * step 1 = spec (with example chips), step 2 = ARON planning (bg task, animated
 * steps), step 3 = the decision + generated code + sample, applied or discarded
 * from right here. Closing mid-plan hands the task off to the page banner.
 */
function AddColumnModal({ sheet, options, replaceId, initialLabel, initialDescription, mut, startRefreshTask, handoffPlanTask, onClose }: {
  sheet: AiSheet;
  options: ReturnType<typeof useSheetOptions>["data"];
  replaceId?: string;
  initialLabel?: string;
  initialDescription?: string;
  mut: Mut;
  startRefreshTask: (id: string) => void;
  handoffPlanTask: (id: string) => void;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<"ai" | "system" | "manual">("ai");
  const [manualFmt, setManualFmt] = useState("text");
  const [label, setLabel] = useState(initialLabel ?? "");
  const [desc, setDesc] = useState(initialDescription ?? "");
  const [field, setField] = useState("");
  const [err, setErr] = useState("");
  const [planTaskId, setPlanTaskId] = useState<string | null>(null);
  const [chatTaskId, setChatTaskId] = useState<string | null>(null);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const planTask = useTaskPolling(planTaskId);
  const chatTask = useTaskPolling(chatTaskId);
  const tickTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const logRef = useRef<HTMLDivElement | null>(null);

  const dim = options?.dimensions.find((d) => d.key === sheet.row_dimension);
  const usedFields = new Set(
    sheet.columns.filter((c) => c.kind === "system").map((c) => c.config.field),
  );
  const freeFields = (dim?.system_columns ?? []).filter((f) => !usedFields.has(f.field));

  // Elapsed-seconds ticker + autoscroll of the live task log.
  useEffect(() => {
    if (!planTaskId) return;
    setElapsed(0);
    tickTimer.current = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => { if (tickTimer.current) clearInterval(tickTimer.current); };
  }, [planTaskId]);
  useEffect(() => {
    logRef.current?.scrollTo({ top: logRef.current.scrollHeight });
  }, [planTask.data?.log]);
  useEffect(() => {
    const st = planTask.data?.status;
    if (!st || !["success", "failure", "cancelled"].includes(st)) return;
    if (tickTimer.current) clearInterval(tickTimer.current);
    if (st === "success") {
      const colId = (planTask.data!.result as { column_id?: string } | null)?.column_id;
      mut.invalidate();
      setDraftId(colId ?? null);
      setPlanTaskId(null);
      if (!colId) setErr("Plan gotowy, ale nie udało się odczytać szkicu — odśwież stronę.");
    } else {
      setPlanTaskId(null);
      setErr(planTask.data?.error || "Planowanie nie powiodło się — spróbuj ponownie.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planTask.data?.status]);

  // A reply from ARON changes the draft in place (or leaves it alone and just
  // answers) — either way the sheet has to be refetched for the thread to show it.
  useEffect(() => {
    const st = chatTask.data?.status;
    if (!st || !["success", "failure", "cancelled"].includes(st)) return;
    setChatTaskId(null);
    mut.invalidate();
    if (st !== "success") {
      setErr(chatTask.data?.error || "Rozmowa nie powiodła się — spróbuj ponownie.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatTask.data?.status]);

  const draft = draftId ? sheet.columns.find((c) => c.id === draftId) : null;
  // Only a trial that failed on EVERY row is truly unapplicable. A review doubt is
  // a warning the human may overrule — see the second button below.
  const draftAllFailed = !!draft && draft.kind === "code"
    && (draft.plan.sample?.length ?? 0) > 0 && draft.plan.sample!.every((x) => !x.ok);
  const draftDoubt = draft?.plan.review?.verdict === "unanswerable";
  const planning = !!planTaskId;

  const close = () => {
    // Mid-plan close: the task keeps running — hand it to the page-level banner
    // so the draft still lands (and opens) when ARON finishes.
    if (planTaskId) handoffPlanTask(planTaskId);
    onClose();
  };

  const startPlan = () => {
    if (!label.trim()) { setErr("Podaj nazwę kolumny."); return; }
    setErr("");
    mut.planColumn.mutate(
      { label: label.trim(), description: desc.trim(), replace_column_id: replaceId },
      {
        onSuccess: (r) => setPlanTaskId(r.task_id),
        onError: (e) => setErr(apiErrText(e)),
      },
    );
  };

  const logLines = (planTask.data?.log ?? "").split("\n").filter(Boolean);

  return (
    <ModalShell
      title={replaceId ? "Przelicz kolumnę z nowym opisem" : "Nowa kolumna"}
      size={draft || planning ? "lg" : "md"}
      onClose={close}
      footer={
        draft ? (
          <>
            <Button size="sm" variant="ghost" onClick={() => {
              mut.discardColumn.mutate(draft.id);
              setDraftId(null);
            }} title="Odrzuca szkic razem z rozmową i wraca do pustego opisu">
              Zacznij od nowa
            </Button>
            {!draftAllFailed && (
              <Button size="sm" variant={draftDoubt ? "secondary" : undefined}
                loading={mut.applyColumn.isPending}
                title={draftDoubt ? "ARON ma wątpliwość — decyzja należy do Ciebie" : undefined}
                onClick={() =>
                  mut.applyColumn.mutate(
                    { colId: draft.id, force: draftDoubt },
                    {
                      onSuccess: (r) => { startRefreshTask(r.task_id); onClose(); },
                      onError: (e) => setErr(apiErrText(e)),
                    },
                  )
                }>
                {draftDoubt
                  ? `Zastosuj mimo ostrzeżenia (${sheet.rows_count} wierszy)`
                  : `Zastosuj do ${sheet.rows_count} wierszy`}
              </Button>
            )}
          </>
        ) : planning ? (
          <Button size="sm" variant="ghost" onClick={close}>Zamknij (planowanie trwa w tle)</Button>
        ) : (
          <>
            <Button size="sm" variant="ghost" onClick={close}>Anuluj</Button>
            {tab === "manual" ? (
              <Button size="sm" loading={mut.addManualColumn.isPending} onClick={() => {
                if (!label.trim()) { setErr("Podaj nazwę kolumny."); return; }
                mut.addManualColumn.mutate(
                  { label: label.trim(), fmt: manualFmt },
                  { onSuccess: onClose, onError: (e) => setErr(apiErrText(e)) },
                );
              }}>
                Dodaj kolumnę ręczną
              </Button>
            ) : tab === "system" ? (
              <Button size="sm" disabled={!field || !options} loading={mut.addSystemColumn.isPending} onClick={() =>
                mut.addSystemColumn.mutate(field, { onSuccess: onClose, onError: (e) => setErr(apiErrText(e)) })
              }>
                Dodaj kolumnę
              </Button>
            ) : (
              <Button size="sm" loading={mut.planColumn.isPending} onClick={startPlan}>
                Dalej — pokaż podejście ARON-a
              </Button>
            )}
          </>
        )
      }
    >
      {draft ? (
        <div>
          <div className="flex items-center gap-2">
            <KindBadge kind={draft.kind} />
            <b className="text-sm">{draft.label}</b>
          </div>
          <ColumnChat
            col={draft}
            mut={mut}
            readOnly={false}
            running={!!chatTaskId}
            logText={chatTask.data?.log ?? ""}
            onSent={(r) => { setErr(""); setChatTaskId(r.task_id); }}
            setErr={setErr}
          />
          {draftAllFailed && draft.plan.review?.verdict !== "unanswerable" && (
            <div className="mt-2 text-xs bg-red-50 border-l-[3px] border-red-400 text-red-800 p-2.5">
              Próba <b>nie przeszła na żadnym wierszu</b> mimo poprawek — nie da się
              zastosować. Napisz ARON-owi w rozmowie, czego brakuje, albo zacznij od
              nowa.
            </div>
          )}
          {draft.code && (
            <>
              <SectionLabel>Wygenerowany skrypt</SectionLabel>
              <PythonCodeBlock code={draft.code} theme="monokai" fontSize="0.68rem" />
            </>
          )}
          {draft.plan.review?.verdict === "unanswerable"
            && (draft.plan.sample?.length ?? 0) === 0 && (
            <ReviewNote review={draft.plan.review}
                        showProblem={(draft.chat?.length ?? 0) === 0} />
          )}
          {(draft.plan.sample?.length ?? 0) > 0 && (
            <>
              <SectionLabel>Próba na wierszach</SectionLabel>
              <SampleTable sample={draft.plan.sample!} />
              {draft.plan.review && (
                <ReviewNote review={draft.plan.review}
                            showProblem={(draft.chat?.length ?? 0) === 0} />
              )}
              <p className="font-mono text-[0.62rem] text-[var(--color-text-muted)] mt-1">
                Sprawdź, czy wartości mają sens — dopiero „Zastosuj” liczy całość.
              </p>
            </>
          )}
          {draft.kind === "analysis" && (
            <>
              <SectionLabel>Koszt</SectionLabel>
              <p className="text-xs">
                Każda komórka = osobna analiza (limit wywołań narzędzi). Szacunkowo{" "}
                <b>{fmtUsd(draft.cost_per_cell)}</b>/komórkę → {sheet.rows_count} wierszy ≈{" "}
                <b>{fmtUsd(Number(draft.cost_per_cell) * sheet.rows_count)}</b> za przeliczenie kolumny.
              </p>
            </>
          )}
          <p className="font-mono text-[0.62rem] text-[var(--color-text-muted)] mt-3">
            {draft.kind === "code"
              ? `utworzenie: ~${fmtUsd(draft.plan.cost_usd ?? 0)} (jednorazowo) · odświeżanie: bez tokenów`
              : `koszt planowania: ${fmtUsd(draft.plan.cost_usd ?? 0)}`}
            {draft.plan.repairs ? ` · poprawki kodu po próbie: ${draft.plan.repairs}` : ""}
            {replaceId ? " · po zastosowaniu podmieni obecną kolumnę" : ""}
          </p>
        </div>
      ) : planning ? (
        <div>
          <div className="flex items-center gap-2 text-xs text-[var(--color-text-secondary)]">
            <Spinner size="sm" />
            <span>
              ARON pracuje nad kolumną <b>„{label}”</b> — na żywo poniżej.
            </span>
            <span className="ml-auto font-mono text-[0.66rem] text-[var(--color-text-muted)]">
              {Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, "0")}
            </span>
          </div>
          <div
            ref={logRef}
            className="mt-2 h-[46vh] overflow-y-auto bg-[#161616] text-[#d9d9d9] font-mono text-[0.66rem] leading-relaxed p-3.5 whitespace-pre-wrap border border-black"
          >
            {logLines.length === 0 ? "Startuję zadanie w tle…" : logLines.join("\n")}
          </div>
          <p className="font-mono text-[0.6rem] text-[var(--color-text-muted)] mt-1.5">
            Generacja + próba w piaskownicy trwa zwykle 1–3 min. Możesz zamknąć — szkic
            i tak przyjdzie (baner na arkuszu).
          </p>
        </div>
      ) : (
        <>
          {!replaceId && (
            <div className="flex gap-1.5 mb-3">
              {(["ai", "system", "manual"] as const).map((t) => (
                <button key={t} onClick={() => setTab(t)}
                  className={`px-3 py-1 font-mono text-[0.64rem] font-bold uppercase border ${
                    tab === t ? "border-[var(--color-text-primary)] border-b-2 border-b-[var(--color-primary)]" : "border-[var(--color-border)] text-[var(--color-text-muted)]"
                  }`}>
                  {t === "ai" ? "Kolumna AI" : t === "system" ? "Pole systemowe" : "Ręczna"}
                </button>
              ))}
            </div>
          )}
          {tab === "manual" ? (
            <div className="space-y-3">
              <label className="block">
                <span className="section-label">Nazwa kolumny</span>
                <input className="input-base w-full" value={label} autoFocus
                       onChange={(e) => setLabel(e.target.value)}
                       placeholder="np. Ustalenia z klientem" />
              </label>
              <label className="block">
                <span className="section-label">Format wartości</span>
                <select className="input-base w-full" value={manualFmt}
                        onChange={(e) => setManualFmt(e.target.value)}>
                  <option value="text">Tekst</option>
                  <option value="number">Liczba</option>
                  <option value="money">Kwota (PLN)</option>
                  <option value="percent">Procent</option>
                  <option value="date">Data</option>
                  <option value="flag">Tak/nie</option>
                  <option value="level">Poziom (niski/średni/wysoki)</option>
                </select>
              </label>
              <div className="text-[0.68rem] text-[var(--color-text-muted)] bg-[var(--color-bg-elevated)] border-l-[3px] border-[var(--color-border-strong)] p-2.5">
                Wartości wpisujesz sam (klik w komórkę). Odświeżanie arkusza ich nigdy
                nie nadpisze, a <b>kolumny AI po prawej mogą z nich korzystać</b> — to
                sposób na wiedzę, którą masz tylko Ty.
              </div>
            </div>
          ) : tab === "system" ? (
            !options ? (
              <div className="flex items-center gap-2 py-3 text-xs text-[var(--color-text-muted)]"><Spinner size="sm" /> Wczytuję pola…</div>
            ) : (
              <label className="block">
                <span className="section-label">Pole z portfela ({sheet.row_dimension_label})</span>
                <select className="input-base w-full" value={field} onChange={(e) => setField(e.target.value)}>
                  <option value="">— wybierz —</option>
                  {freeFields.map((f) => (
                    <option key={f.field} value={f.field}>{f.label}</option>
                  ))}
                </select>
              </label>
            )
          ) : (
            <div className="space-y-3">
              <label className="block">
                <span className="section-label">Nazwa kolumny (wymagana)</span>
                <input className="input-base w-full" value={label} onChange={(e) => setLabel(e.target.value)}
                       placeholder="np. % fakturowania" autoFocus />
              </label>
              <label className="block">
                <span className="section-label">Jak to liczyć (opcjonalnie — bez opisu ARON zinterpretuje samą nazwę)</span>
                <textarea className="input-base w-full min-h-[72px]" value={desc} onChange={(e) => setDesc(e.target.value)}
                          placeholder="np. kwota faktur PRZ podpiętych do zamówień przedsięwzięcia jako procent wartości umowy" />
              </label>
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="font-mono text-[0.62rem] text-[var(--color-text-muted)]">przykłady:</span>
                {CANNED.map((c) => (
                  <button
                    key={c.name}
                    onClick={() => { setLabel(c.name); setDesc(c.desc); }}
                    className="px-2 py-0.5 font-mono text-[0.62rem] border border-dashed border-[var(--color-border-strong)] text-[var(--color-text-secondary)] bg-[var(--color-bg-elevated)] hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]"
                  >
                    {c.name}
                  </button>
                ))}
              </div>
              <div className="text-[0.68rem] text-[var(--color-text-muted)] bg-[var(--color-bg-elevated)] border-l-[3px] border-[var(--color-border-strong)] p-2.5">
                ARON sam zdecyduje: <KindBadge kind="code" /> (skrypt raz, odświeżanie bez tokenów) czy{" "}
                <KindBadge kind="analysis" /> (AI liczy każdą komórkę). Zobaczysz decyzję, próbkę
                na wierszach i koszt — zanim policzy całość.
                {replaceId && <> Po zastosowaniu nowa wersja <b>podmieni</b> obecną kolumnę.</>}
              </div>
            </div>
          )}
        </>
      )}
      {err && <p className="text-xs text-red-700 mt-2">{err}</p>}
    </ModalShell>
  );
}

function AddRowModal({ sheetId, mut, onClose }: { sheetId: string; mut: Mut; onClose: () => void }) {
  const [q, setQ] = useState("");
  const candidates = useRowCandidates(sheetId, q, true);
  return (
    <ModalShell title="Dodaj wiersz" onClose={onClose}
      footer={<Button size="sm" variant="ghost" onClick={onClose}>Zamknij</Button>}>
      <input className="input-base w-full" placeholder="Szukaj obiektu…" value={q}
             onChange={(e) => setQ(e.target.value)} autoFocus />
      <div className="mt-2 max-h-72 overflow-y-auto divide-y divide-[var(--color-border-muted)]">
        {(candidates.data ?? []).map((c) => (
          <div key={c.entity_id} className="flex items-center gap-2 py-1.5 text-xs">
            <div className="min-w-0">
              <div className="font-semibold">{c.label}</div>
              <div className="font-mono text-[0.62rem] text-[var(--color-text-muted)]">{c.sublabel}</div>
            </div>
            <span className="flex-1" />
            {c.in_sheet ? (
              <Badge color="gray">w arkuszu</Badge>
            ) : (
              <Button size="sm" variant="secondary" onClick={() => mut.rowsAdd.mutate(c.entity_id)}>
                Dodaj
              </Button>
            )}
          </div>
        ))}
        {candidates.data?.length === 0 && (
          <p className="py-3 text-xs text-[var(--color-text-muted)]">Brak wyników.</p>
        )}
      </div>
    </ModalShell>
  );
}

/* ── misc ──────────────────────────────────────────────────────────────────── */

function apiErrText(e: unknown): string {
  const detail = (e as { response?: { data?: { errors?: { detail?: string } | string[] } } })
    ?.response?.data?.errors;
  if (typeof detail === "object" && detail && "detail" in detail) return String((detail as { detail?: string }).detail);
  if (Array.isArray(detail)) return detail.join("; ");
  return "Nie udało się — spróbuj ponownie.";
}
